import { fetchLowStockItemExportRows } from '@/renderer/services/inventoryDashboardService';
import { fetchInventoryItems } from '@/renderer/services/inventoryItemService';
import { fetchIngredientCosts } from '@/renderer/services/inventoryRecipeService';
import { fetchInventoryStockBalanceExportRows } from '@/renderer/services/inventoryStockBalanceService';
import {
  fetchInventoryStockMovementExportRows,
  fetchInventoryStockMovementItemSummary,
} from '@/renderer/services/inventoryStockMovementService';
import { fetchSupplierInvoiceExportRows } from '@/renderer/services/inventorySupplierInvoiceService';
import { exportReportCsv } from '@/renderer/services/reportsService';
import type {
  InventoryDashboardLowStockRow,
  InventoryItemRecord,
  InventoryItemSummaryCounts,
  InventoryRecipeRecord,
  InventoryRecipeSummaryCounts,
  InventoryStockBalanceRecord,
  InventoryStockBalanceSummaryCounts,
  InventoryStockLocationRecord,
  InventoryStockLocationSummaryCounts,
  InventoryStockMovementItemOptionRecord,
  InventoryStockMovementItemSummaryRow,
  InventoryStockMovementRecord,
  InventoryStockMovementSummaryCounts,
  InventorySupplierInvoiceRecord,
  InventorySupplierInvoiceSummaryCounts,
  InventorySupplierRecord,
  InventorySupplierSummaryCounts,
  StockAdjustmentRecord,
  StockTransferRecord,
} from '@/renderer/types/inventory';
import type { PaymentMethodRecord } from '@/renderer/types/accounting';
import { apiError, apiSuccess } from '@/renderer/util/notify';
import {
  formatReportDateTime,
  formatReportMoney,
} from '@/renderer/util/reporting';
import {
  Button,
  ConfirmDialog,
  DropdownSelect,
  EmptyState,
  LoadingState,
  PaginationControls,
} from '@components/common';
import { usePaymentMethods } from '@hooks/accounting';
import {
  useDeleteInventoryItem,
  useDeleteInventoryStockLocation,
  useDeleteInventorySupplier,
  useDeleteRecipe,
  useDeleteSupplierInvoice,
  useInventoryCategories,
  useInventoryItems,
  useInventoryItemSelectOptions,
  useInventoryItemSummary,
  useInventoryLowStockItems,
  useInventoryRecipes,
  useInventoryRecipeSummary,
  useInventoryStockBalances,
  useInventoryStockBalanceSummary,
  useInventoryStockLocations,
  useInventoryStockLocationSummary,
  useInventoryStockMovementItemOptions,
  useInventoryStockMovementItemSummary,
  useInventoryStockMovements,
  useInventoryStockMovementSummary,
  useInventorySupplierInvoiceDetail,
  useInventorySupplierInvoices,
  useInventorySupplierInvoiceSummary,
  useInventorySupplierPayments,
  useInventorySuppliers,
  useInventorySupplierSummary,
  useInventoryUnits,
  usePostStockAdjustment,
  usePostStockTransfer,
  usePostSupplierInvoice,
  useRecordSupplierPayment,
  useSaveInventoryCategory,
  useSaveInventoryItem,
  useSaveInventoryStockBalance,
  useSaveInventoryStockLocation,
  useSaveInventorySupplier,
  useSaveInventoryUnit,
  useSaveStockAdjustment,
  useSaveStockTransfer,
  useSaveSupplierInvoice,
  useSetDefaultInventoryStockLocation,
  useStockAdjustments,
  useStockAdjustmentSummary,
  useStockTransferDetail,
  useStockTransfers,
  useStockTransferSummary,
} from '@hooks/inventory';
import { useAppSelector } from '@hooks/useRedux';
import { selectCurrentUser } from '@store/auth/authSelectors';
import { selectEffectiveOutlet, selectEffectiveOutletId } from '@store/outlets';
import React, { useDeferredValue, useEffect, useMemo, useState } from 'react';
import { StockAdjustmentEditorModal } from './adjustments/StockAdjustmentEditorModal';
import { StockBalanceEditorModal } from './balances/StockBalanceEditorModal';
import { StockBalancesSummaryCards } from './balances/StockBalancesSummaryCards';
import { StockBalancesTable } from './balances/StockBalancesTable';
import { StockBalancesToolbar } from './balances/StockBalancesToolbar';
import {
  buildSaveInventoryStockBalancePayload,
  createEmptyInventoryStockBalanceForm,
  createInventoryStockBalanceFormFromRecord,
  InventoryStockBalanceFormState,
} from './balances/stockBalanceForm';
import { InventoryDashboardPageContent } from './dashboard/InventoryDashboardPageContent';
import { SupplierInvoiceEditorModal } from './invoices/SupplierInvoiceEditorModal';
import { SupplierInvoicesSummaryCards } from './invoices/SupplierInvoicesSummaryCards';
import { SupplierInvoicesTable } from './invoices/SupplierInvoicesTable';
import { SupplierInvoicesToolbar } from './invoices/SupplierInvoicesToolbar';
import { SupplierPaymentModal } from './invoices/SupplierPaymentModal';
import {
  buildSaveSupplierInvoicePayload,
  createEmptySupplierInvoiceForm,
  createSupplierInvoiceFormFromRecord,
  SupplierInvoiceFormState,
} from './invoices/supplierInvoiceForm';
import { InventoryItemEditorModal } from './items/InventoryItemEditorModal';
import { InventoryItemsSummaryCards } from './items/InventoryItemsSummaryCards';
import { InventoryItemsTable } from './items/InventoryItemsTable';
import { InventoryItemsToolbar } from './items/InventoryItemsToolbar';
import {
  buildSaveInventoryItemPayload,
  createEmptyInventoryItemForm,
  createInventoryItemFormFromRecord,
  InventoryItemFormState,
} from './items/inventoryItemForm';
import { StockLocationEditorModal } from './locations/StockLocationEditorModal';
import { StockLocationsSummaryCards } from './locations/StockLocationsSummaryCards';
import { StockLocationsTable } from './locations/StockLocationsTable';
import { StockLocationsToolbar } from './locations/StockLocationsToolbar';
import {
  buildSaveInventoryStockLocationPayload,
  createEmptyInventoryStockLocationForm,
  createInventoryStockLocationFormFromRecord,
  InventoryStockLocationFormState,
} from './locations/stockLocationForm';
import { StockMovementSummaryTable } from './movements/StockMovementSummaryTable';
import { StockMovementsSummaryCards } from './movements/StockMovementsSummaryCards';
import { StockMovementsTable } from './movements/StockMovementsTable';
import { StockMovementsToolbar } from './movements/StockMovementsToolbar';
import { formatInventoryStockMovementType } from './movements/stockMovementForm';
import { MenuItemsMissingRecipesPage as MenuItemsMissingRecipesPageContent } from './recipes/MenuItemsMissingRecipesPage';
import { RecipeEditorModal } from './recipes/RecipeEditorModal';
import { RecipesSummaryCards } from './recipes/RecipesSummaryCards';
import { RecipesTable } from './recipes/RecipesTable';
import { RecipesToolbar } from './recipes/RecipesToolbar';
import { useRecipeEditorController } from './recipes/useRecipeEditorController';
import { InventoryReportsPage as InventoryReportsPageContent } from './reports/InventoryReportsPage';
import { ReturnToSupplierPage as ReturnToSupplierPageContent } from './returns/ReturnToSupplierPage';
import { InventoryCategoriesPageContent } from './settings/InventoryCategoriesPageContent';
import { InventoryUnitsPageContent } from './settings/InventoryUnitsPageContent';
import { StockCountPage as StockCountPageContent } from './stockcount/StockCountPage';
import { SupplierEditorModal } from './suppliers/SupplierEditorModal';
import { SupplierPaymentsTable } from './suppliers/SupplierPaymentsTable';
import { SuppliersSummaryCards } from './suppliers/SuppliersSummaryCards';
import { SuppliersTable } from './suppliers/SuppliersTable';
import { SuppliersToolbar } from './suppliers/SuppliersToolbar';
import {
  buildSaveInventorySupplierPayload,
  createEmptyInventorySupplierForm,
  createInventorySupplierFormFromRecord,
  InventorySupplierFormState,
} from './suppliers/supplierForm';
import { StockTransferEditorModal } from './transfers/StockTransferEditorModal';

export {
  InventoryReportsPageContent as InventoryReportsPage,
  MenuItemsMissingRecipesPageContent as MenuItemsMissingRecipesPage,
  ReturnToSupplierPageContent as ReturnToSupplierPage,
  StockCountPageContent as StockCountPage,
};

export const InventoryDashboardPage: React.FC = () => (
  <InventoryDashboardPageContent />
);

const STOCK_MOVEMENT_ITEM_OPTION_PAGE_SIZE = 20;
const ITEM_FILTER_OPTION_PAGE_SIZE = 20;
const STOCK_MOVEMENT_CSV_COLUMNS = [
  { key: 'movement_date', header: 'Date' },
  { key: 'item', header: 'Item' },
  { key: 'sku', header: 'SKU' },
  { key: 'location', header: 'Location' },
  { key: 'movement_type', header: 'Movement' },
  { key: 'quantity', header: 'Quantity' },
  { key: 'unit_cost', header: 'Unit cost' },
  { key: 'total_cost', header: 'Total cost' },
  { key: 'reference_type', header: 'Reference type' },
  { key: 'reference', header: 'Reference' },
];
const STOCK_MOVEMENT_SUMMARY_CSV_COLUMNS = [
  { key: 'item', header: 'Item' },
  { key: 'sku', header: 'SKU' },
  { key: 'quantity_in', header: 'Quantity in' },
  { key: 'quantity_out', header: 'Quantity out' },
  { key: 'net_quantity', header: 'Net quantity' },
  { key: 'total_value', header: 'Value' },
  { key: 'movement_count', header: 'Movement count' },
];
const STOCK_BALANCE_CSV_COLUMNS = [
  { key: 'item', header: 'Item' },
  { key: 'sku', header: 'SKU' },
  { key: 'location', header: 'Location' },
  { key: 'quantity_on_hand', header: 'Quantity on hand' },
  { key: 'average_cost', header: 'Average cost' },
  { key: 'total_value', header: 'Total value' },
];
const SUPPLIER_INVOICE_CSV_COLUMNS = [
  { key: 'invoice_number', header: 'Invoice number' },
  { key: 'supplier', header: 'Supplier' },
  { key: 'location', header: 'Location' },
  { key: 'invoice_date', header: 'Invoice date' },
  { key: 'due_date', header: 'Due date' },
  { key: 'status', header: 'Status' },
  { key: 'total_amount', header: 'Total amount' },
  { key: 'amount_paid', header: 'Amount paid' },
  { key: 'outstanding', header: 'Outstanding' },
];
const LOW_STOCK_CSV_COLUMNS = [
  { key: 'item', header: 'Item' },
  { key: 'sku', header: 'SKU' },
  { key: 'location', header: 'Location' },
  { key: 'quantity_on_hand', header: 'Quantity on hand' },
  { key: 'reorder_level', header: 'Reorder level' },
  { key: 'deficit_quantity', header: 'Deficit quantity' },
  { key: 'total_value', header: 'Stock value' },
];

const sanitizeCsvFileSegment = (value: string): string =>
  value.replace(/[^a-z0-9-_]+/gi, '-').replace(/-+/g, '-');

const formatStockMovementReferenceType = (value: string | null): string => {
  if (!value) return 'No reference type';
  return value
    .replace(/_/g, ' ')
    .replace(/\b\w/g, (char) => char.toUpperCase());
};

const formatStockMovementReferenceValue = (
  movement: InventoryStockMovementRecord,
): string => {
  if (movement.reference_type === 'order') {
    return movement.order_bill_number
      ? `Bill #${movement.order_bill_number}`
      : 'Bill number unavailable';
  }

  return movement.reference_id || 'No reference ID';
};

// Matches the 2-decimal, no-currency-prefix formatting used inline in
// invoices/SupplierInvoicesTable.tsx — kept local since that formatting
// isn't exported as a shared helper.
const formatTwoDecimalAmount = (value: number) =>
  value.toLocaleString(undefined, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });

const buildStockMovementCsvRows = (
  movements: InventoryStockMovementRecord[],
  items: InventoryItemRecord[],
  locations: InventoryStockLocationRecord[],
) => {
  const itemsById = new Map(items.map((item) => [item.id, item]));
  const locationsById = new Map(
    locations.map((location) => [location.id, location]),
  );

  return movements.map((movement) => {
    const item = itemsById.get(movement.inventory_item_id);
    const location = locationsById.get(movement.stock_location_id);

    return {
      movement_date: formatReportDateTime(movement.movement_date),
      item: item?.name || movement.inventory_item_id,
      sku: item?.sku || '',
      location: location?.name || movement.stock_location_id,
      movement_type: formatInventoryStockMovementType(movement.movement_type),
      quantity: movement.quantity,
      unit_cost:
        movement.unit_cost === null
          ? ''
          : formatReportMoney(movement.unit_cost),
      total_cost:
        movement.total_cost === null
          ? ''
          : formatReportMoney(movement.total_cost),
      reference_type: formatStockMovementReferenceType(movement.reference_type),
      reference: formatStockMovementReferenceValue(movement),
    };
  });
};

const buildStockMovementSummaryCsvRows = (
  rows: InventoryStockMovementItemSummaryRow[],
) =>
  rows.map((row) => ({
    item: row.item_name || row.inventory_item_id,
    sku: row.sku || '',
    quantity_in: row.quantity_in,
    quantity_out: row.quantity_out,
    net_quantity: row.net_quantity,
    total_value: formatReportMoney(row.total_value),
    movement_count: row.movement_count,
  }));

const buildStockBalanceCsvRows = (
  balances: InventoryStockBalanceRecord[],
  items: InventoryItemRecord[],
  locations: InventoryStockLocationRecord[],
) => {
  const itemsById = new Map(items.map((item) => [item.id, item]));
  const locationsById = new Map(
    locations.map((location) => [location.id, location]),
  );

  return balances.map((balance) => {
    const item = itemsById.get(balance.inventory_item_id);
    const location = locationsById.get(balance.stock_location_id);

    return {
      item: item?.name || balance.inventory_item_id,
      sku: item?.sku || '',
      location: location?.name || balance.stock_location_id,
      quantity_on_hand: balance.quantity_on_hand,
      average_cost: formatReportMoney(balance.average_cost),
      total_value: formatReportMoney(balance.total_value),
    };
  });
};

const buildSupplierInvoiceCsvRows = (
  invoices: InventorySupplierInvoiceRecord[],
  suppliers: InventorySupplierRecord[],
  locations: InventoryStockLocationRecord[],
) => {
  const suppliersById = new Map(
    suppliers.map((supplier) => [supplier.id, supplier]),
  );
  const locationsById = new Map(
    locations.map((location) => [location.id, location]),
  );

  return invoices.map((invoice) => ({
    invoice_number: invoice.invoice_number || '',
    supplier:
      suppliersById.get(invoice.supplier_id)?.name || invoice.supplier_id,
    location:
      locationsById.get(invoice.stock_location_id)?.name ||
      invoice.stock_location_id,
    invoice_date: invoice.invoice_date,
    due_date: invoice.due_date || '',
    status: invoice.status,
    total_amount: formatTwoDecimalAmount(invoice.total_amount),
    amount_paid: formatTwoDecimalAmount(invoice.amount_paid),
    outstanding: formatTwoDecimalAmount(
      invoice.total_amount - invoice.amount_paid,
    ),
  }));
};

const buildLowStockCsvRows = (rows: InventoryDashboardLowStockRow[]) =>
  rows.map((row) => ({
    item: row.item_name,
    sku: row.sku || '',
    location: row.stock_location_name,
    quantity_on_hand: row.quantity_on_hand,
    reorder_level: row.reorder_level,
    deficit_quantity: row.deficit_quantity,
    total_value: formatReportMoney(row.total_value),
  }));

function useStockMovementItemFilterOptions({
  companyId,
  outletId,
  stockLocationId,
  movementType,
  dateFrom,
  dateTo,
}: {
  companyId: number | null;
  outletId: string | null;
  stockLocationId: string;
  movementType: InventoryStockMovementRecord['movement_type'] | 'all';
  dateFrom?: string;
  dateTo?: string;
}) {
  const [searchQuery, setSearchQuery] = useState('');
  const deferredSearchQuery = useDeferredValue(searchQuery);
  const [page, setPage] = useState(1);
  const [loadedOptions, setLoadedOptions] = useState<
    InventoryStockMovementItemOptionRecord[]
  >([]);

  const optionsQuery = useInventoryStockMovementItemOptions({
    companyId: companyId ?? 0,
    outletId: outletId ?? '',
    stockLocationId,
    movementType,
    dateFrom,
    dateTo,
    search: deferredSearchQuery,
    page,
    pageSize: STOCK_MOVEMENT_ITEM_OPTION_PAGE_SIZE,
  });

  useEffect(() => {
    setPage(1);
    setLoadedOptions([]);
  }, [
    companyId,
    outletId,
    stockLocationId,
    movementType,
    dateFrom,
    dateTo,
    deferredSearchQuery,
  ]);

  useEffect(() => {
    const nextOptions = optionsQuery.data?.data;
    if (!nextOptions) return;

    setLoadedOptions((previous) => {
      const merged = page === 1 ? nextOptions : [...previous, ...nextOptions];
      return Array.from(
        new Map(merged.map((option) => [option.id, option])).values(),
      );
    });
  }, [optionsQuery.data?.data, page]);

  const options = useMemo(
    () =>
      loadedOptions.map((item) => ({
        value: item.id,
        label: item.sku ? `${item.name} (${item.sku})` : item.name,
      })),
    [loadedOptions],
  );

  return {
    options,
    isLoading: optionsQuery.isFetching,
    hasMore: Boolean(optionsQuery.data?.pagination.hasNextPage),
    setSearchQuery,
    loadMore: () => {
      if (optionsQuery.data?.pagination.hasNextPage) {
        setPage((current) => current + 1);
      }
    },
  };
}

function useInventoryItemFilterOptions({
  companyId,
  status = 'all',
  selectedItemIds = [],
}: {
  companyId: number | null;
  status?: 'all' | 'active' | 'inactive';
  selectedItemIds?: string[];
}) {
  return useInventoryItemSelectOptions({
    companyId,
    status,
    selectedItemIds,
    pageSize: ITEM_FILTER_OPTION_PAGE_SIZE,
  });
}

export const InventoryItemsPage: React.FC = () => {
  const currentUser = useAppSelector(selectCurrentUser);
  const companyId = currentUser?.company?.id ?? null;

  const [searchQuery, setSearchQuery] = useState('');
  const deferredSearchQuery = useDeferredValue(searchQuery);
  const [categoryFilter, setCategoryFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState<
    'all' | 'active' | 'inactive'
  >('all');
  const [page, setPage] = useState(1);
  const [pageSize] = useState(20);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingItem, setEditingItem] = useState<InventoryItemRecord | null>(
    null,
  );
  const [itemToDelete, setItemToDelete] = useState<InventoryItemRecord | null>(
    null,
  );

  const itemsQuery = useInventoryItems({
    companyId: companyId ?? 0,
    page,
    pageSize,
    search: deferredSearchQuery,
    categoryId: categoryFilter,
    status: statusFilter,
  });
  const summaryQuery = useInventoryItemSummary(
    companyId,
    deferredSearchQuery,
    categoryFilter,
    statusFilter,
  );
  const categoriesQuery = useInventoryCategories(companyId);
  const unitsQuery = useInventoryUnits(companyId);
  const { mutateAsync: saveItemAsync, isPending: isSaving } =
    useSaveInventoryItem();
  const { mutateAsync: saveCategoryAsync, isPending: isSavingCategory } =
    useSaveInventoryCategory();
  const { mutateAsync: saveUnitAsync, isPending: isSavingUnit } =
    useSaveInventoryUnit();
  const { mutateAsync: deleteItemAsync, isPending: isDeleting } =
    useDeleteInventoryItem();

  const items = itemsQuery.data?.data ?? [];
  const pagination = itemsQuery.data?.pagination;
  const categories = categoriesQuery.data ?? [];
  const units = unitsQuery.data ?? [];
  const summary: InventoryItemSummaryCounts = summaryQuery.data ?? {
    totalItems: 0,
    activeItems: 0,
    inactiveItems: 0,
    categoryCount: 0,
  };

  const openCreateModal = () => {
    setEditingItem(null);
    setIsModalOpen(true);
  };

  const openEditModal = (item: InventoryItemRecord) => {
    setEditingItem(item);
    setIsModalOpen(true);
  };

  const handleSubmit = async (form: InventoryItemFormState) => {
    if (!companyId) {
      return;
    }

    const categoryName = form.newCategoryName.trim();
    const purchaseUnitName = form.newPurchaseUnitName.trim();
    const stockUnitName = form.newStockUnitName.trim();

    const createdUnits = new Map<string, string>();
    let categoryId = form.categoryId || null;
    let { purchaseUnitId, stockUnitId } = form;

    if (categoryName) {
      const existingCategory = categories.find(
        (candidate) =>
          candidate.name.trim().toLowerCase() === categoryName.toLowerCase(),
      );

      if (existingCategory) {
        categoryId = existingCategory.id;
      } else {
        const savedCategory = await saveCategoryAsync({
          companyId,
          name: categoryName,
        });
        categoryId = savedCategory.id;
      }
    }

    const resolveUnitId = async (
      nextName: string,
      selectedId: string,
    ): Promise<string> => {
      const trimmedName = nextName.trim();
      if (!trimmedName) {
        return selectedId;
      }

      const key = trimmedName.toLowerCase();
      const existingId = createdUnits.get(key);
      if (existingId) {
        return existingId;
      }

      const existingUnit = units.find(
        (candidate) => candidate.name.trim().toLowerCase() === key,
      );
      if (existingUnit) {
        createdUnits.set(key, existingUnit.id);
        return existingUnit.id;
      }

      const savedUnit = await saveUnitAsync({
        companyId,
        name: trimmedName,
      });
      createdUnits.set(key, savedUnit.id);
      return savedUnit.id;
    };

    purchaseUnitId = await resolveUnitId(purchaseUnitName, purchaseUnitId);
    stockUnitId = await resolveUnitId(stockUnitName, stockUnitId);

    if (!purchaseUnitId || !stockUnitId) {
      apiError('Select or create both purchase and stock units before saving.');
      return;
    }

    await saveItemAsync(
      buildSaveInventoryItemPayload(
        companyId,
        {
          ...form,
          categoryId: categoryId || '',
          purchaseUnitId,
          stockUnitId,
        },
        editingItem?.id,
      ),
    );
    setIsModalOpen(false);
    setEditingItem(null);
  };

  const handleDelete = async () => {
    if (!companyId || !itemToDelete) {
      return;
    }

    await deleteItemAsync({ companyId, itemId: itemToDelete.id });
    setItemToDelete(null);
  };

  if (!companyId) {
    return (
      <EmptyState
        title="Inventory unavailable"
        description="We could not find a company context for this inventory workspace."
      />
    );
  }

  if (itemsQuery.isLoading) {
    return <LoadingState message="Loading inventory items..." />;
  }

  if (itemsQuery.isError) {
    return (
      <EmptyState
        title="Inventory items unavailable"
        description={
          itemsQuery.error?.message ||
          'Unable to load inventory items right now.'
        }
        primaryAction={{
          label: 'Try again',
          onClick: () => itemsQuery.refetch(),
        }}
      />
    );
  }

  return (
    <div className="space-y-6">
      <InventoryItemsToolbar
        searchQuery={searchQuery}
        categoryFilter={categoryFilter}
        statusFilter={statusFilter}
        categories={categories}
        onSearchChange={(value) => {
          setSearchQuery(value.toString());
          setPage(1);
        }}
        onCategoryChange={(value) => {
          setCategoryFilter(value);
          setPage(1);
        }}
        onStatusChange={(value) => {
          setStatusFilter(value);
          setPage(1);
        }}
        onCreate={openCreateModal}
      />

      <InventoryItemsSummaryCards summary={summary} />

      {items.length ? (
        <>
          <InventoryItemsTable
            items={items}
            categories={categories}
            units={units}
            onEdit={openEditModal}
            onDelete={setItemToDelete}
          />

          {pagination ? (
            <PaginationControls
              pagination={pagination}
              itemLabel="items"
              onPageChange={setPage}
            />
          ) : null}
        </>
      ) : (
        <EmptyState
          title="No inventory items yet"
          description="Create your first inventory item to define shared stock master data for all outlets in the company."
          primaryAction={{ label: 'Create item', onClick: openCreateModal }}
        />
      )}

      {isModalOpen ? (
        <InventoryItemEditorModal
          initial={
            editingItem
              ? createInventoryItemFormFromRecord(editingItem)
              : createEmptyInventoryItemForm()
          }
          categories={categories}
          units={units}
          isSaving={isSaving || isSavingCategory || isSavingUnit}
          title={editingItem ? 'Edit inventory item' : 'Create inventory item'}
          onClose={() => {
            if (!(isSaving || isSavingCategory || isSavingUnit)) {
              setIsModalOpen(false);
              setEditingItem(null);
            }
          }}
          onSubmit={handleSubmit}
        />
      ) : null}

      <ConfirmDialog
        isOpen={!!itemToDelete}
        onClose={() => setItemToDelete(null)}
        onConfirm={handleDelete}
        isLoading={isDeleting}
        title="Delete inventory item"
        message={`Delete ${itemToDelete?.name || 'this inventory item'}? This should only be done if it has not yet been used in stock, purchasing, or recipe workflows.`}
        confirmText="Delete item"
        tone="danger"
      />
    </div>
  );
};

export const StockOverviewPage: React.FC = () => {
  const currentUser = useAppSelector(selectCurrentUser);
  const effectiveOutletId = useAppSelector(selectEffectiveOutletId);
  const effectiveOutlet = useAppSelector(selectEffectiveOutlet);
  const companyId = currentUser?.company?.id ?? null;

  const [locationSearchQuery, setLocationSearchQuery] = useState('');
  const deferredLocationSearchQuery = useDeferredValue(locationSearchQuery);
  const [typeFilter, setTypeFilter] = useState<
    InventoryStockLocationRecord['type'] | 'all'
  >('all');
  const [statusFilter, setStatusFilter] = useState<
    'all' | 'active' | 'inactive'
  >('all');
  const [page, setPage] = useState(1);
  const [pageSize] = useState(20);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingLocation, setEditingLocation] =
    useState<InventoryStockLocationRecord | null>(null);
  const [locationToDelete, setLocationToDelete] =
    useState<InventoryStockLocationRecord | null>(null);

  const [balanceLocationFilter, setBalanceLocationFilter] = useState('all');
  const [balanceGroupByLocation, setBalanceGroupByLocation] = useState(false);
  const [balanceItemFilterIds, setBalanceItemFilterIds] = useState<string[]>(
    [],
  );
  const [balancePage, setBalancePage] = useState(1);
  const [balancePageSize] = useState(20);
  const [isBalanceModalOpen, setIsBalanceModalOpen] = useState(false);
  const [editingBalance, setEditingBalance] =
    useState<InventoryStockBalanceRecord | null>(null);
  const [movementDateFrom, setMovementDateFrom] = useState('');
  const [movementDateTo, setMovementDateTo] = useState('');
  const [movementLocationFilter, setMovementLocationFilter] = useState('all');
  const [movementTypeFilter, setMovementTypeFilter] = useState<
    InventoryStockMovementRecord['movement_type'] | 'all'
  >('all');
  const [movementItemFilterIds, setMovementItemFilterIds] = useState<string[]>(
    [],
  );
  const [movementPage, setMovementPage] = useState(1);
  const [movementPageSize] = useState(20);
  const [isMovementExporting, setIsMovementExporting] = useState(false);

  const locationsQuery = useInventoryStockLocations({
    companyId: companyId ?? 0,
    outletId: effectiveOutletId ?? '',
    page,
    pageSize,
    search: deferredLocationSearchQuery,
    type: typeFilter,
    status: statusFilter,
  });
  const summaryQuery = useInventoryStockLocationSummary(
    companyId,
    effectiveOutletId,
    deferredLocationSearchQuery,
    typeFilter,
    statusFilter,
  );
  const allLocationsQuery = useInventoryStockLocations({
    companyId: companyId ?? 0,
    outletId: effectiveOutletId ?? '',
    page: 1,
    pageSize: 200,
    search: '',
    type: 'all',
    status: 'active',
  });
  const balancesQuery = useInventoryStockBalances({
    companyId: companyId ?? 0,
    outletId: effectiveOutletId ?? '',
    page: balancePage,
    pageSize: balancePageSize,
    stockLocationId: balanceLocationFilter,
    inventoryItemIds: balanceItemFilterIds,
  });
  const balanceDisplayItemIds = useMemo(
    () =>
      Array.from(
        new Set(
          (balancesQuery.data?.data ?? []).map(
            (balance) => balance.inventory_item_id,
          ),
        ),
      ),
    [balancesQuery.data?.data],
  );
  const balanceItemsQuery = useInventoryItems({
    companyId: companyId ?? 0,
    page: 1,
    pageSize: Math.max(balanceDisplayItemIds.length, 1),
    search: '',
    categoryId: 'all',
    status: 'all',
    itemIds: balanceDisplayItemIds,
  });
  const balanceItemOptions = useInventoryItemFilterOptions({
    companyId,
    status: 'all',
    selectedItemIds: balanceItemFilterIds,
  });
  const balanceSummaryQuery = useInventoryStockBalanceSummary(
    companyId,
    effectiveOutletId,
    '',
    balanceLocationFilter,
    balanceItemFilterIds,
  );
  const movementsQuery = useInventoryStockMovements({
    companyId: companyId ?? 0,
    outletId: effectiveOutletId ?? '',
    page: movementPage,
    pageSize: movementPageSize,
    stockLocationId: movementLocationFilter,
    movementType: movementTypeFilter,
    inventoryItemIds: movementItemFilterIds,
    dateFrom: movementDateFrom || undefined,
    dateTo: movementDateTo || undefined,
  });
  const movementSummaryQuery = useInventoryStockMovementSummary(
    companyId,
    effectiveOutletId,
    '',
    movementLocationFilter,
    movementTypeFilter,
    movementItemFilterIds,
    movementDateFrom || undefined,
    movementDateTo || undefined,
  );
  const movementItemOptions = useStockMovementItemFilterOptions({
    companyId,
    outletId: effectiveOutletId,
    stockLocationId: movementLocationFilter,
    movementType: movementTypeFilter,
    dateFrom: movementDateFrom || undefined,
    dateTo: movementDateTo || undefined,
  });
  const { mutateAsync: saveLocationAsync, isPending: isSaving } =
    useSaveInventoryStockLocation();
  const { mutateAsync: deleteLocationAsync, isPending: isDeleting } =
    useDeleteInventoryStockLocation();
  const { mutate: setDefaultLocation, isPending: isSettingDefaultLocation } =
    useSetDefaultInventoryStockLocation();
  const { mutateAsync: saveBalanceAsync, isPending: isSavingBalance } =
    useSaveInventoryStockBalance();
  const unitsQueryOverview = useInventoryUnits(companyId);
  const overviewUnits = unitsQueryOverview.data ?? [];

  const locations = locationsQuery.data?.data ?? [];
  const pagination = locationsQuery.data?.pagination;
  const allLocations = allLocationsQuery.data?.data ?? [];
  const balanceItems = useMemo(
    () =>
      Array.from(
        new Map(
          [
            ...(balanceItemsQuery.data?.data ?? []),
            ...balanceItemOptions.items,
          ].map((item) => [item.id, item]),
        ).values(),
      ),
    [balanceItemOptions.items, balanceItemsQuery.data?.data],
  );
  const balances = balancesQuery.data?.data ?? [];
  const balancePagination = balancesQuery.data?.pagination;
  const movements = movementsQuery.data?.data ?? [];
  const movementPagination = movementsQuery.data?.pagination;
  const summary: InventoryStockLocationSummaryCounts = summaryQuery.data ?? {
    totalLocations: 0,
    activeLocations: 0,
    inactiveLocations: 0,
    uniqueTypes: 0,
  };
  const balanceSummary: InventoryStockBalanceSummaryCounts =
    balanceSummaryQuery.data ?? {
      totalBalanceRows: 0,
      totalQuantityOnHand: 0,
      totalStockValue: 0,
      lowStockItems: 0,
    };
  const movementSummary: InventoryStockMovementSummaryCounts =
    movementSummaryQuery.data ?? {
      totalMovements: 0,
      totalQuantityIn: 0,
      totalQuantityOut: 0,
      totalMovementValue: 0,
    };
  const outletName = effectiveOutlet?.name ?? 'this outlet';

  const handleMovementExportCsv = async () => {
    if (!companyId || !effectiveOutletId) {
      apiError('Company or outlet context is missing for CSV export.');
      return;
    }

    setIsMovementExporting(true);

    try {
      const exportRowsResult = await fetchInventoryStockMovementExportRows({
        companyId,
        outletId: effectiveOutletId,
        stockLocationId: movementLocationFilter,
        movementType: movementTypeFilter,
        inventoryItemIds: movementItemFilterIds,
        dateFrom: movementDateFrom || undefined,
        dateTo: movementDateTo || undefined,
      });

      if (!exportRowsResult.success || !exportRowsResult.data) {
        apiError(
          exportRowsResult.error ||
            'Failed to load stock movements for CSV export.',
        );
        return;
      }

      if (!exportRowsResult.data.length) {
        apiError(
          'No stock movements match the current filters for CSV export.',
        );
        return;
      }

      const response = await exportReportCsv({
        title: 'Stock movements',
        fileName: [
          'stock-movements',
          sanitizeCsvFileSegment(effectiveOutlet?.name || 'outlet'),
          new Date().toISOString().slice(0, 10),
        ].join('-'),
        columns: STOCK_MOVEMENT_CSV_COLUMNS,
        rows: buildStockMovementCsvRows(
          exportRowsResult.data,
          balanceItems,
          allLocations,
        ),
      });

      if (!response.success || !response.data) {
        apiError(response.error || 'Failed to export stock movements CSV.');
        return;
      }

      apiSuccess('Stock movements CSV export completed successfully.');
    } finally {
      setIsMovementExporting(false);
    }
  };

  const openCreateModal = () => {
    setEditingLocation(null);
    setIsModalOpen(true);
  };

  const openEditModal = (location: InventoryStockLocationRecord) => {
    setEditingLocation(location);
    setIsModalOpen(true);
  };

  const handleSubmit = async (form: InventoryStockLocationFormState) => {
    if (!companyId || !effectiveOutletId) {
      return;
    }

    await saveLocationAsync(
      buildSaveInventoryStockLocationPayload(
        companyId,
        effectiveOutletId,
        form,
        editingLocation?.id,
      ),
    );
    setIsModalOpen(false);
    setEditingLocation(null);
  };

  const handleDelete = async () => {
    if (!companyId || !effectiveOutletId || !locationToDelete) {
      return;
    }

    await deleteLocationAsync({
      companyId,
      outletId: effectiveOutletId,
      locationId: locationToDelete.id,
    });
    setLocationToDelete(null);
  };

  const openCreateBalanceModal = () => {
    setEditingBalance(null);
    setIsBalanceModalOpen(true);
  };

  const openEditBalanceModal = (balance: InventoryStockBalanceRecord) => {
    setEditingBalance(balance);
    setIsBalanceModalOpen(true);
  };

  const handleBalanceSubmit = async (form: InventoryStockBalanceFormState) => {
    if (!companyId || !effectiveOutletId) {
      return;
    }

    await saveBalanceAsync(
      buildSaveInventoryStockBalancePayload(
        companyId,
        effectiveOutletId,
        form,
        editingBalance?.id,
      ),
    );
    setIsBalanceModalOpen(false);
    setEditingBalance(null);
  };

  if (!companyId) {
    return (
      <EmptyState
        title="Stock locations unavailable"
        description="We could not find a company context for this inventory workspace."
      />
    );
  }

  if (!effectiveOutletId) {
    return (
      <EmptyState
        title="Choose an outlet first"
        description="Stock locations belong to a specific outlet. Pick an outlet from the header before managing storage locations."
      />
    );
  }

  if (locationsQuery.isLoading) {
    return <LoadingState message="Loading stock locations..." />;
  }

  if (locationsQuery.isError) {
    return (
      <EmptyState
        title="Stock locations unavailable"
        description={
          locationsQuery.error?.message ||
          'Unable to load stock locations right now.'
        }
        primaryAction={{
          label: 'Try again',
          onClick: () => locationsQuery.refetch(),
        }}
      />
    );
  }

  return (
    <div className="space-y-6">
      <StockLocationsToolbar
        outletName={outletName}
        searchQuery={locationSearchQuery}
        typeFilter={typeFilter}
        statusFilter={statusFilter}
        onSearchChange={(value) => {
          setLocationSearchQuery(value);
          setPage(1);
        }}
        onTypeChange={(value) => {
          setTypeFilter(value as InventoryStockLocationRecord['type'] | 'all');
          setPage(1);
        }}
        onStatusChange={(value) => {
          setStatusFilter(value as 'all' | 'active' | 'inactive');
          setPage(1);
        }}
        onCreate={openCreateModal}
      />

      <StockLocationsSummaryCards summary={summary} />

      {locations.length ? (
        <>
          <StockLocationsTable
            locations={locations}
            onEdit={openEditModal}
            onDelete={setLocationToDelete}
            onSetDefault={(location) => setDefaultLocation(location.id)}
            isSettingDefault={isSettingDefaultLocation}
          />
          {pagination ? (
            <PaginationControls
              pagination={pagination}
              itemLabel="locations"
              onPageChange={setPage}
            />
          ) : null}
        </>
      ) : (
        <EmptyState
          title="No stock locations yet"
          description={`Create the first stock location for ${outletName} so inventory balances and future stock movements have a storage point to work from.`}
          primaryAction={{
            label: 'Create location',
            onClick: openCreateModal,
          }}
        />
      )}

      {isModalOpen ? (
        <StockLocationEditorModal
          initial={
            editingLocation
              ? createInventoryStockLocationFormFromRecord(editingLocation)
              : createEmptyInventoryStockLocationForm()
          }
          isSaving={isSaving}
          title={
            editingLocation ? 'Edit stock location' : 'Create stock location'
          }
          onClose={() => {
            if (!isSaving) {
              setIsModalOpen(false);
              setEditingLocation(null);
            }
          }}
          onSubmit={handleSubmit}
        />
      ) : null}

      <StockBalancesToolbar
        stockLocationFilter={balanceLocationFilter}
        itemFilterIds={balanceItemFilterIds}
        locations={allLocations}
        itemOptions={balanceItemOptions.options}
        itemOptionsLoading={balanceItemOptions.isLoading}
        itemOptionsHasMore={balanceItemOptions.hasMore}
        groupByLocation={balanceGroupByLocation}
        onStockLocationChange={(value) => {
          setBalanceLocationFilter(value);
          setBalancePage(1);
        }}
        onItemFilterChange={(value) => {
          setBalanceItemFilterIds(value);
          setBalancePage(1);
        }}
        onItemSearchChange={balanceItemOptions.setSearchQuery}
        onLoadMoreItems={balanceItemOptions.loadMore}
        onCreate={openCreateBalanceModal}
        onGroupByLocationChange={setBalanceGroupByLocation}
      />

      <StockBalancesSummaryCards summary={balanceSummary} />

      {balances.length ? (
        <>
          <StockBalancesTable
            balances={balances}
            items={balanceItems}
            locations={allLocations}
            units={overviewUnits}
            isLoading={balancesQuery.isFetching && !balancesQuery.isLoading}
            groupByLocation={
              balanceGroupByLocation && balanceLocationFilter === 'all'
            }
            onEdit={openEditBalanceModal}
          />
          {balancePagination ? (
            <PaginationControls
              pagination={balancePagination}
              itemLabel="balances"
              onPageChange={setBalancePage}
            />
          ) : null}
        </>
      ) : (
        <EmptyState
          title="No stock balances yet"
          description={`Set the first stock balance for ${outletName} so the outlet starts with visible quantity on hand and stock value.`}
          primaryAction={{
            label: 'Set balance',
            onClick: openCreateBalanceModal,
          }}
        />
      )}

      {isBalanceModalOpen ? (
        <StockBalanceEditorModal
          companyId={companyId ?? 0}
          initial={
            editingBalance
              ? createInventoryStockBalanceFormFromRecord(editingBalance)
              : createEmptyInventoryStockBalanceForm()
          }
          locations={allLocations}
          isSaving={isSavingBalance}
          title={editingBalance ? 'Edit stock balance' : 'Set stock balance'}
          onClose={() => {
            if (!isSavingBalance) {
              setIsBalanceModalOpen(false);
              setEditingBalance(null);
            }
          }}
          onSubmit={handleBalanceSubmit}
        />
      ) : null}

      <StockMovementsToolbar
        dateFrom={movementDateFrom}
        dateTo={movementDateTo}
        stockLocationFilter={movementLocationFilter}
        movementTypeFilter={movementTypeFilter}
        itemFilterIds={movementItemFilterIds}
        locations={allLocations}
        itemOptions={movementItemOptions.options}
        itemOptionsLoading={movementItemOptions.isLoading}
        itemOptionsHasMore={movementItemOptions.hasMore}
        isExporting={isMovementExporting}
        canExport={
          movements.length > 0 || Boolean(movementPagination?.totalItems)
        }
        onDateFromChange={(value) => {
          setMovementDateFrom(value);
          setMovementPage(1);
        }}
        onDateToChange={(value) => {
          setMovementDateTo(value);
          setMovementPage(1);
        }}
        onStockLocationChange={(value) => {
          setMovementLocationFilter(value);
          setMovementPage(1);
        }}
        onMovementTypeChange={(value) => {
          setMovementTypeFilter(
            value as InventoryStockMovementRecord['movement_type'] | 'all',
          );
          setMovementPage(1);
        }}
        onItemFilterChange={(value) => {
          setMovementItemFilterIds(value);
          setMovementPage(1);
        }}
        onItemSearchChange={movementItemOptions.setSearchQuery}
        onLoadMoreItems={movementItemOptions.loadMore}
        onExportCsv={handleMovementExportCsv}
      />

      <StockMovementsSummaryCards summary={movementSummary} />

      {movements.length ? (
        <>
          <StockMovementsTable
            movements={movements}
            items={balanceItems}
            locations={allLocations}
            units={overviewUnits}
            isLoading={movementsQuery.isFetching && !movementsQuery.isLoading}
          />
          {movementPagination ? (
            <PaginationControls
              pagination={movementPagination}
              itemLabel="movements"
              onPageChange={setMovementPage}
            />
          ) : null}
        </>
      ) : (
        <EmptyState
          title="No stock movements yet"
          description="Movement history will appear here once purchasing, transfers, adjustments, and sales consumption begin posting stock transactions into the outlet ledger."
        />
      )}

      <ConfirmDialog
        isOpen={!!locationToDelete}
        onClose={() => setLocationToDelete(null)}
        onConfirm={handleDelete}
        isLoading={isDeleting}
        title="Delete stock location"
        message={`Delete ${locationToDelete?.name || 'this stock location'}? This should only be done if it has not yet been used by stock balance, recipe, transfer, or adjustment workflows.`}
        confirmText="Delete location"
        tone="danger"
      />
    </div>
  );
};

export const StockLocationsPage: React.FC = () => {
  const currentUser = useAppSelector(selectCurrentUser);
  const effectiveOutletId = useAppSelector(selectEffectiveOutletId);
  const effectiveOutlet = useAppSelector(selectEffectiveOutlet);
  const companyId = currentUser?.company?.id ?? null;

  const [searchQuery, setSearchQuery] = useState('');
  const deferredSearchQuery = useDeferredValue(searchQuery);
  const [typeFilter, setTypeFilter] = useState<
    InventoryStockLocationRecord['type'] | 'all'
  >('all');
  const [statusFilter, setStatusFilter] = useState<
    'all' | 'active' | 'inactive'
  >('all');
  const [page, setPage] = useState(1);
  const [pageSize] = useState(20);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingLocation, setEditingLocation] =
    useState<InventoryStockLocationRecord | null>(null);
  const [locationToDelete, setLocationToDelete] =
    useState<InventoryStockLocationRecord | null>(null);

  const locationsQuery = useInventoryStockLocations({
    companyId: companyId ?? 0,
    outletId: effectiveOutletId ?? '',
    page,
    pageSize,
    search: deferredSearchQuery,
    type: typeFilter,
    status: statusFilter,
  });
  const summaryQuery = useInventoryStockLocationSummary(
    companyId,
    effectiveOutletId,
    deferredSearchQuery,
    typeFilter,
    statusFilter,
  );
  const { mutateAsync: saveLocationAsync, isPending: isSaving } =
    useSaveInventoryStockLocation();
  const { mutateAsync: deleteLocationAsync, isPending: isDeleting } =
    useDeleteInventoryStockLocation();
  const { mutate: setDefaultLocation, isPending: isSettingDefaultLocation } =
    useSetDefaultInventoryStockLocation();

  const locations = locationsQuery.data?.data ?? [];
  const pagination = locationsQuery.data?.pagination;
  const summary: InventoryStockLocationSummaryCounts = summaryQuery.data ?? {
    totalLocations: 0,
    activeLocations: 0,
    inactiveLocations: 0,
    uniqueTypes: 0,
  };
  const outletName = effectiveOutlet?.name ?? 'this outlet';

  const openCreateModal = () => {
    setEditingLocation(null);
    setIsModalOpen(true);
  };

  const openEditModal = (location: InventoryStockLocationRecord) => {
    setEditingLocation(location);
    setIsModalOpen(true);
  };

  const handleSubmit = async (form: InventoryStockLocationFormState) => {
    if (!companyId || !effectiveOutletId) return;
    await saveLocationAsync(
      buildSaveInventoryStockLocationPayload(
        companyId,
        effectiveOutletId,
        form,
        editingLocation?.id,
      ),
    );
    setIsModalOpen(false);
    setEditingLocation(null);
  };

  const handleDelete = async () => {
    if (!companyId || !effectiveOutletId || !locationToDelete) return;
    await deleteLocationAsync({
      companyId,
      outletId: effectiveOutletId,
      locationId: locationToDelete.id,
    });
    setLocationToDelete(null);
  };

  if (!companyId) {
    return (
      <EmptyState
        title="Stock locations unavailable"
        description="We could not find a company context for this inventory workspace."
      />
    );
  }

  if (!effectiveOutletId) {
    return (
      <EmptyState
        title="Choose an outlet first"
        description="Stock locations belong to a specific outlet. Pick an outlet from the header before managing storage locations."
      />
    );
  }

  if (locationsQuery.isLoading) {
    return <LoadingState message="Loading stock locations..." />;
  }

  if (locationsQuery.isError) {
    return (
      <EmptyState
        title="Stock locations unavailable"
        description={
          locationsQuery.error?.message ||
          'Unable to load stock locations right now.'
        }
        primaryAction={{
          label: 'Try again',
          onClick: () => locationsQuery.refetch(),
        }}
      />
    );
  }

  return (
    <div className="space-y-6">
      <StockLocationsToolbar
        outletName={outletName}
        searchQuery={searchQuery}
        typeFilter={typeFilter}
        statusFilter={statusFilter}
        onSearchChange={(value) => {
          setSearchQuery(value);
          setPage(1);
        }}
        onTypeChange={(value) => {
          setTypeFilter(value as InventoryStockLocationRecord['type'] | 'all');
          setPage(1);
        }}
        onStatusChange={(value) => {
          setStatusFilter(value as 'all' | 'active' | 'inactive');
          setPage(1);
        }}
        onCreate={openCreateModal}
      />

      <StockLocationsSummaryCards summary={summary} />

      {locations.length ? (
        <>
          <StockLocationsTable
            locations={locations}
            onEdit={openEditModal}
            onDelete={setLocationToDelete}
            onSetDefault={(location) => setDefaultLocation(location.id)}
            isSettingDefault={isSettingDefaultLocation}
          />
          {pagination ? (
            <PaginationControls
              pagination={pagination}
              itemLabel="locations"
              onPageChange={setPage}
            />
          ) : null}
        </>
      ) : (
        <EmptyState
          title="No stock locations yet"
          description={`Create the first stock location for ${outletName} so inventory balances and future stock movements have a storage point to work from.`}
          primaryAction={{
            label: 'Create location',
            onClick: openCreateModal,
          }}
        />
      )}

      {isModalOpen ? (
        <StockLocationEditorModal
          initial={
            editingLocation
              ? createInventoryStockLocationFormFromRecord(editingLocation)
              : createEmptyInventoryStockLocationForm()
          }
          isSaving={isSaving}
          title={
            editingLocation ? 'Edit stock location' : 'Create stock location'
          }
          onClose={() => {
            if (!isSaving) {
              setIsModalOpen(false);
              setEditingLocation(null);
            }
          }}
          onSubmit={handleSubmit}
        />
      ) : null}

      <ConfirmDialog
        isOpen={!!locationToDelete}
        onClose={() => setLocationToDelete(null)}
        onConfirm={handleDelete}
        isLoading={isDeleting}
        title="Delete stock location"
        message={`Delete ${locationToDelete?.name || 'this stock location'}? This should only be done if it has not yet been used by stock balance, recipe, transfer, or adjustment workflows.`}
        confirmText="Delete location"
        tone="danger"
      />
    </div>
  );
};

export const StockBalancesPage: React.FC = () => {
  const currentUser = useAppSelector(selectCurrentUser);
  const effectiveOutletId = useAppSelector(selectEffectiveOutletId);
  const effectiveOutlet = useAppSelector(selectEffectiveOutlet);
  const companyId = currentUser?.company?.id ?? null;

  const [locationFilter, setLocationFilter] = useState('all');
  const [groupByLocation, setGroupByLocation] = useState(false);
  const [itemFilterIds, setItemFilterIds] = useState<string[]>([]);
  const [page, setPage] = useState(1);
  const [pageSize] = useState(20);
  const [isExporting, setIsExporting] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingBalance, setEditingBalance] =
    useState<InventoryStockBalanceRecord | null>(null);

  const allLocationsQuery = useInventoryStockLocations({
    companyId: companyId ?? 0,
    outletId: effectiveOutletId ?? '',
    page: 1,
    pageSize: 200,
    search: '',
    type: 'all',
    status: 'active',
  });
  const balancesQuery = useInventoryStockBalances({
    companyId: companyId ?? 0,
    outletId: effectiveOutletId ?? '',
    page,
    pageSize,
    stockLocationId: locationFilter,
    inventoryItemIds: itemFilterIds,
  });
  const balanceDisplayItemIds = useMemo(
    () =>
      Array.from(
        new Set(
          (balancesQuery.data?.data ?? []).map(
            (balance) => balance.inventory_item_id,
          ),
        ),
      ),
    [balancesQuery.data?.data],
  );
  const itemsQuery = useInventoryItems({
    companyId: companyId ?? 0,
    page: 1,
    pageSize: Math.max(balanceDisplayItemIds.length, 1),
    search: '',
    categoryId: 'all',
    status: 'all',
    itemIds: balanceDisplayItemIds,
  });
  const unitsQuery = useInventoryUnits(companyId);
  const balanceItemOptions = useInventoryItemFilterOptions({
    companyId,
    status: 'all',
    selectedItemIds: itemFilterIds,
  });
  const balanceSummaryQuery = useInventoryStockBalanceSummary(
    companyId,
    effectiveOutletId,
    '',
    locationFilter,
    itemFilterIds,
  );
  const { mutateAsync: saveBalanceAsync, isPending: isSaving } =
    useSaveInventoryStockBalance();

  const allLocations = allLocationsQuery.data?.data ?? [];
  const items = useMemo(
    () =>
      Array.from(
        new Map(
          [...(itemsQuery.data?.data ?? []), ...balanceItemOptions.items].map(
            (item) => [item.id, item],
          ),
        ).values(),
      ),
    [balanceItemOptions.items, itemsQuery.data?.data],
  );
  const units = unitsQuery.data ?? [];
  const balances = balancesQuery.data?.data ?? [];
  const pagination = balancesQuery.data?.pagination;
  const summary: InventoryStockBalanceSummaryCounts =
    balanceSummaryQuery.data ?? {
      totalBalanceRows: 0,
      totalQuantityOnHand: 0,
      totalStockValue: 0,
      lowStockItems: 0,
    };
  const outletName = effectiveOutlet?.name ?? 'this outlet';

  const openCreateModal = () => {
    setEditingBalance(null);
    setIsModalOpen(true);
  };

  const openEditModal = (balance: InventoryStockBalanceRecord) => {
    setEditingBalance(balance);
    setIsModalOpen(true);
  };

  const handleSubmit = async (form: InventoryStockBalanceFormState) => {
    if (!companyId || !effectiveOutletId) return;
    await saveBalanceAsync(
      buildSaveInventoryStockBalancePayload(
        companyId,
        effectiveOutletId,
        form,
        editingBalance?.id,
      ),
    );
    setIsModalOpen(false);
    setEditingBalance(null);
  };

  const handleExportCsv = async () => {
    if (!companyId || !effectiveOutletId) {
      apiError('Company or outlet context is missing for CSV export.');
      return;
    }

    setIsExporting(true);

    try {
      const exportRowsResult = await fetchInventoryStockBalanceExportRows({
        companyId,
        outletId: effectiveOutletId,
        stockLocationId: locationFilter,
        inventoryItemIds: itemFilterIds,
      });

      if (!exportRowsResult.success || !exportRowsResult.data) {
        apiError(
          exportRowsResult.error ||
            'Failed to load stock balances for CSV export.',
        );
        return;
      }

      if (!exportRowsResult.data.length) {
        apiError('No stock balances match the current filters for CSV export.');
        return;
      }

      const exportItemIds = Array.from(
        new Set(
          exportRowsResult.data.map((balance) => balance.inventory_item_id),
        ),
      );
      const exportItemsResult = await fetchInventoryItems({
        companyId,
        page: 1,
        pageSize: Math.max(exportItemIds.length, 1),
        search: '',
        categoryId: 'all',
        status: 'all',
        itemIds: exportItemIds,
      });

      if (!exportItemsResult.success || !exportItemsResult.data) {
        apiError(
          exportItemsResult.error ||
            'Failed to load inventory item details for CSV export.',
        );
        return;
      }

      const response = await exportReportCsv({
        title: 'Stock balances',
        fileName: [
          'stock-balances',
          sanitizeCsvFileSegment(effectiveOutlet?.name || 'outlet'),
          new Date().toISOString().slice(0, 10),
        ].join('-'),
        columns: STOCK_BALANCE_CSV_COLUMNS,
        rows: buildStockBalanceCsvRows(
          exportRowsResult.data,
          exportItemsResult.data.data,
          allLocations,
        ),
      });

      if (!response.success || !response.data) {
        apiError(response.error || 'Failed to export stock balances CSV.');
        return;
      }

      apiSuccess('Stock balances CSV export completed successfully.');
    } finally {
      setIsExporting(false);
    }
  };

  if (!companyId) {
    return (
      <EmptyState
        title="Stock balances unavailable"
        description="We could not find a company context for this inventory workspace."
      />
    );
  }

  if (!effectiveOutletId) {
    return (
      <EmptyState
        title="Choose an outlet first"
        description="Stock balances are outlet-specific. Pick an outlet from the header to continue."
      />
    );
  }

  if (balancesQuery.isLoading) {
    return <LoadingState message="Loading stock balances..." />;
  }

  return (
    <div className="space-y-6">
      <StockBalancesToolbar
        stockLocationFilter={locationFilter}
        itemFilterIds={itemFilterIds}
        locations={allLocations}
        itemOptions={balanceItemOptions.options}
        itemOptionsLoading={balanceItemOptions.isLoading}
        itemOptionsHasMore={balanceItemOptions.hasMore}
        isExporting={isExporting}
        canExport={balances.length > 0 || Boolean(pagination?.totalItems)}
        groupByLocation={groupByLocation}
        onStockLocationChange={(value) => {
          setLocationFilter(value);
          setPage(1);
        }}
        onItemFilterChange={(value) => {
          setItemFilterIds(value);
          setPage(1);
        }}
        onItemSearchChange={balanceItemOptions.setSearchQuery}
        onLoadMoreItems={balanceItemOptions.loadMore}
        onExportCsv={handleExportCsv}
        onCreate={openCreateModal}
        onGroupByLocationChange={setGroupByLocation}
      />

      <StockBalancesSummaryCards summary={summary} />

      {balances.length ? (
        <>
          <StockBalancesTable
            balances={balances}
            items={items}
            locations={allLocations}
            units={units}
            isLoading={balancesQuery.isFetching && !balancesQuery.isLoading}
            groupByLocation={groupByLocation && locationFilter === 'all'}
            onEdit={openEditModal}
          />
          {pagination ? (
            <PaginationControls
              pagination={pagination}
              itemLabel="balances"
              onPageChange={setPage}
            />
          ) : null}
        </>
      ) : (
        <EmptyState
          title="No stock balances yet"
          description={`Set the first stock balance for ${outletName} so the outlet starts with visible quantity on hand and stock value.`}
          primaryAction={{
            label: 'Set balance',
            onClick: openCreateModal,
          }}
        />
      )}

      {isModalOpen ? (
        <StockBalanceEditorModal
          companyId={companyId}
          initial={
            editingBalance
              ? createInventoryStockBalanceFormFromRecord(editingBalance)
              : createEmptyInventoryStockBalanceForm()
          }
          locations={allLocations}
          isSaving={isSaving}
          title={editingBalance ? 'Edit stock balance' : 'Set stock balance'}
          onClose={() => {
            if (!isSaving) {
              setIsModalOpen(false);
              setEditingBalance(null);
            }
          }}
          onSubmit={handleSubmit}
        />
      ) : null}
    </div>
  );
};

export const StockMovementsPage: React.FC = () => {
  const currentUser = useAppSelector(selectCurrentUser);
  const effectiveOutletId = useAppSelector(selectEffectiveOutletId);
  const effectiveOutlet = useAppSelector(selectEffectiveOutlet);
  const companyId = currentUser?.company?.id ?? null;

  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [locationFilter, setLocationFilter] = useState('all');
  const [typeFilter, setTypeFilter] = useState<
    InventoryStockMovementRecord['movement_type'] | 'all'
  >('all');
  const [itemFilterIds, setItemFilterIds] = useState<string[]>([]);
  const [page, setPage] = useState(1);
  const [pageSize] = useState(20);
  const [isExporting, setIsExporting] = useState(false);

  const allLocationsQuery = useInventoryStockLocations({
    companyId: companyId ?? 0,
    outletId: effectiveOutletId ?? '',
    page: 1,
    pageSize: 200,
    search: '',
    type: 'all',
    status: 'active',
  });
  const itemsQuery = useInventoryItems({
    companyId: companyId ?? 0,
    page: 1,
    pageSize: 200,
    search: '',
    categoryId: 'all',
    status: 'active',
  });
  const movementsQuery = useInventoryStockMovements({
    companyId: companyId ?? 0,
    outletId: effectiveOutletId ?? '',
    page,
    pageSize,
    stockLocationId: locationFilter,
    movementType: typeFilter,
    inventoryItemIds: itemFilterIds,
    dateFrom: dateFrom || undefined,
    dateTo: dateTo || undefined,
  });
  const movementSummaryQuery = useInventoryStockMovementSummary(
    companyId,
    effectiveOutletId,
    '',
    locationFilter,
    typeFilter,
    itemFilterIds,
    dateFrom || undefined,
    dateTo || undefined,
  );
  const movementItemOptions = useStockMovementItemFilterOptions({
    companyId,
    outletId: effectiveOutletId,
    stockLocationId: locationFilter,
    movementType: typeFilter,
    dateFrom: dateFrom || undefined,
    dateTo: dateTo || undefined,
  });

  const unitsQueryMov = useInventoryUnits(companyId);
  const allLocations = allLocationsQuery.data?.data ?? [];
  const items = itemsQuery.data?.data ?? [];
  const movUnits = unitsQueryMov.data ?? [];
  const movements = movementsQuery.data?.data ?? [];
  const pagination = movementsQuery.data?.pagination;
  const summary: InventoryStockMovementSummaryCounts =
    movementSummaryQuery.data ?? {
      totalMovements: 0,
      totalQuantityIn: 0,
      totalQuantityOut: 0,
      totalMovementValue: 0,
    };

  const handleExportCsv = async () => {
    if (!companyId || !effectiveOutletId) {
      apiError('Company or outlet context is missing for CSV export.');
      return;
    }

    setIsExporting(true);

    try {
      const exportRowsResult = await fetchInventoryStockMovementExportRows({
        companyId,
        outletId: effectiveOutletId,
        stockLocationId: locationFilter,
        movementType: typeFilter,
        inventoryItemIds: itemFilterIds,
        dateFrom: dateFrom || undefined,
        dateTo: dateTo || undefined,
      });

      if (!exportRowsResult.success || !exportRowsResult.data) {
        apiError(
          exportRowsResult.error ||
            'Failed to load stock movements for CSV export.',
        );
        return;
      }

      if (!exportRowsResult.data.length) {
        apiError(
          'No stock movements match the current filters for CSV export.',
        );
        return;
      }

      const response = await exportReportCsv({
        title: 'Stock movements',
        fileName: [
          'stock-movements',
          sanitizeCsvFileSegment(effectiveOutlet?.name || 'outlet'),
          new Date().toISOString().slice(0, 10),
        ].join('-'),
        columns: STOCK_MOVEMENT_CSV_COLUMNS,
        rows: buildStockMovementCsvRows(
          exportRowsResult.data,
          items,
          allLocations,
        ),
      });

      if (!response.success || !response.data) {
        apiError(response.error || 'Failed to export stock movements CSV.');
        return;
      }

      apiSuccess('Stock movements CSV export completed successfully.');
    } finally {
      setIsExporting(false);
    }
  };

  if (!companyId) {
    return (
      <EmptyState
        title="Stock movements unavailable"
        description="We could not find a company context for this inventory workspace."
      />
    );
  }

  if (!effectiveOutletId) {
    return (
      <EmptyState
        title="Choose an outlet first"
        description="Stock movements are outlet-specific. Pick an outlet from the header to continue."
      />
    );
  }

  if (movementsQuery.isLoading) {
    return <LoadingState message="Loading stock movements..." />;
  }

  return (
    <div className="space-y-6">
      <StockMovementsToolbar
        dateFrom={dateFrom}
        dateTo={dateTo}
        stockLocationFilter={locationFilter}
        movementTypeFilter={typeFilter}
        itemFilterIds={itemFilterIds}
        locations={allLocations}
        itemOptions={movementItemOptions.options}
        itemOptionsLoading={movementItemOptions.isLoading}
        itemOptionsHasMore={movementItemOptions.hasMore}
        isExporting={isExporting}
        canExport={movements.length > 0 || Boolean(pagination?.totalItems)}
        onDateFromChange={(value) => {
          setDateFrom(value);
          setPage(1);
        }}
        onDateToChange={(value) => {
          setDateTo(value);
          setPage(1);
        }}
        onStockLocationChange={(value) => {
          setLocationFilter(value);
          setPage(1);
        }}
        onMovementTypeChange={(value) => {
          setTypeFilter(
            value as InventoryStockMovementRecord['movement_type'] | 'all',
          );
          setPage(1);
        }}
        onItemFilterChange={(value) => {
          setItemFilterIds(value);
          setPage(1);
        }}
        onItemSearchChange={movementItemOptions.setSearchQuery}
        onLoadMoreItems={movementItemOptions.loadMore}
        onExportCsv={handleExportCsv}
      />

      <StockMovementsSummaryCards summary={summary} />

      {movements.length ? (
        <>
          <StockMovementsTable
            movements={movements}
            items={items}
            locations={allLocations}
            units={movUnits}
            isLoading={movementsQuery.isFetching && !movementsQuery.isLoading}
          />
          {pagination ? (
            <PaginationControls
              pagination={pagination}
              itemLabel="movements"
              onPageChange={setPage}
            />
          ) : null}
        </>
      ) : (
        <EmptyState
          title="No stock movements yet"
          description="Movement history will appear here once purchasing, transfers, adjustments, and sales consumption begin posting stock transactions into the outlet ledger."
        />
      )}
    </div>
  );
};

const STOCK_MOVEMENT_SUMMARY_PAGE_SIZE = 20;

export const StockMovementSummaryPage: React.FC = () => {
  const currentUser = useAppSelector(selectCurrentUser);
  const effectiveOutletId = useAppSelector(selectEffectiveOutletId);
  const effectiveOutlet = useAppSelector(selectEffectiveOutlet);
  const companyId = currentUser?.company?.id ?? null;

  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [locationFilter, setLocationFilter] = useState('all');
  const [typeFilter, setTypeFilter] = useState<
    InventoryStockMovementRecord['movement_type'] | 'all'
  >('all');
  const [itemFilterIds, setItemFilterIds] = useState<string[]>([]);
  const [page, setPage] = useState(1);
  const [isExporting, setIsExporting] = useState(false);

  const allLocationsQuery = useInventoryStockLocations({
    companyId: companyId ?? 0,
    outletId: effectiveOutletId ?? '',
    page: 1,
    pageSize: 200,
    search: '',
    type: 'all',
    status: 'active',
  });
  const summaryFilters = {
    companyId: companyId ?? 0,
    outletId: effectiveOutletId ?? '',
    stockLocationId: locationFilter,
    movementType: typeFilter,
    inventoryItemIds: itemFilterIds,
    dateFrom: dateFrom || undefined,
    dateTo: dateTo || undefined,
  };
  const itemSummaryQuery = useInventoryStockMovementItemSummary(summaryFilters);
  const movementItemOptions = useStockMovementItemFilterOptions({
    companyId,
    outletId: effectiveOutletId,
    stockLocationId: locationFilter,
    movementType: typeFilter,
    dateFrom: dateFrom || undefined,
    dateTo: dateTo || undefined,
  });

  const allLocations = allLocationsQuery.data?.data ?? [];
  // Already sorted ascending by item name (server-side, via the
  // get-inventory-stock-movement-summary edge function).
  const allRows = itemSummaryQuery.data ?? [];

  const totalItems = allRows.length;
  const totalPages = Math.max(
    1,
    Math.ceil(totalItems / STOCK_MOVEMENT_SUMMARY_PAGE_SIZE),
  );
  const currentPage = Math.min(page, totalPages);
  const pagedRows = allRows.slice(
    (currentPage - 1) * STOCK_MOVEMENT_SUMMARY_PAGE_SIZE,
    currentPage * STOCK_MOVEMENT_SUMMARY_PAGE_SIZE,
  );
  const pagination = {
    page: currentPage,
    pageSize: STOCK_MOVEMENT_SUMMARY_PAGE_SIZE,
    totalItems,
    totalPages,
    hasNextPage: currentPage < totalPages,
    hasPreviousPage: currentPage > 1,
  };

  const handleExportCsv = async () => {
    if (!companyId || !effectiveOutletId) {
      apiError('Company or outlet context is missing for CSV export.');
      return;
    }

    setIsExporting(true);

    try {
      const exportRowsResult =
        await fetchInventoryStockMovementItemSummary(summaryFilters);

      if (!exportRowsResult.success || !exportRowsResult.data) {
        apiError(
          exportRowsResult.error ||
            'Failed to load stock movement summary for CSV export.',
        );
        return;
      }

      if (!exportRowsResult.data.length) {
        apiError(
          'No stock movements match the current filters for CSV export.',
        );
        return;
      }

      const response = await exportReportCsv({
        title: 'Stock movement summary',
        fileName: [
          'stock-movement-summary',
          sanitizeCsvFileSegment(effectiveOutlet?.name || 'outlet'),
          new Date().toISOString().slice(0, 10),
        ].join('-'),
        columns: STOCK_MOVEMENT_SUMMARY_CSV_COLUMNS,
        rows: buildStockMovementSummaryCsvRows(exportRowsResult.data),
      });

      if (!response.success || !response.data) {
        apiError(
          response.error || 'Failed to export stock movement summary CSV.',
        );
        return;
      }

      apiSuccess('Stock movement summary CSV export completed successfully.');
    } finally {
      setIsExporting(false);
    }
  };

  if (!companyId) {
    return (
      <EmptyState
        title="Stock movement summary unavailable"
        description="We could not find a company context for this inventory workspace."
      />
    );
  }

  if (!effectiveOutletId) {
    return (
      <EmptyState
        title="Choose an outlet first"
        description="Stock movement summary is outlet-specific. Pick an outlet from the header to continue."
      />
    );
  }

  if (itemSummaryQuery.isLoading) {
    return <LoadingState message="Loading stock movement summary..." />;
  }

  return (
    <div className="space-y-6">
      <StockMovementsToolbar
        title="Stock movement summary"
        description="See total quantities and value moved per item over the selected period, instead of individual movement rows."
        dateFrom={dateFrom}
        dateTo={dateTo}
        stockLocationFilter={locationFilter}
        movementTypeFilter={typeFilter}
        itemFilterIds={itemFilterIds}
        locations={allLocations}
        itemOptions={movementItemOptions.options}
        itemOptionsLoading={movementItemOptions.isLoading}
        itemOptionsHasMore={movementItemOptions.hasMore}
        isExporting={isExporting}
        canExport={allRows.length > 0}
        onDateFromChange={(value) => {
          setDateFrom(value);
          setPage(1);
        }}
        onDateToChange={(value) => {
          setDateTo(value);
          setPage(1);
        }}
        onStockLocationChange={(value) => {
          setLocationFilter(value);
          setPage(1);
        }}
        onMovementTypeChange={(value) => {
          setTypeFilter(
            value as InventoryStockMovementRecord['movement_type'] | 'all',
          );
          setPage(1);
        }}
        onItemFilterChange={(value) => {
          setItemFilterIds(value);
          setPage(1);
        }}
        onItemSearchChange={movementItemOptions.setSearchQuery}
        onLoadMoreItems={movementItemOptions.loadMore}
        onExportCsv={handleExportCsv}
      />

      {allRows.length ? (
        <>
          <StockMovementSummaryTable
            rows={pagedRows}
            isLoading={
              itemSummaryQuery.isFetching && !itemSummaryQuery.isLoading
            }
          />
          <PaginationControls
            pagination={pagination}
            itemLabel="items"
            onPageChange={setPage}
          />
        </>
      ) : (
        <EmptyState
          title="No stock movements yet"
          description="Aggregated item totals will appear here once purchasing, transfers, adjustments, and sales consumption begin posting stock transactions into the outlet ledger."
        />
      )}
    </div>
  );
};

const SUPPLIER_PAYMENT_METHOD_TYPES: PaymentMethodRecord['type'][] = [
  'cash',
  'card',
  'bank',
  'mobile_money',
  'check',
];

export const PurchaseInvoicesPage: React.FC = () => {
  const currentUser = useAppSelector(selectCurrentUser);
  const effectiveOutletId = useAppSelector(selectEffectiveOutletId);
  const effectiveOutlet = useAppSelector(selectEffectiveOutlet);
  const companyId = currentUser?.company?.id ?? null;

  const [supplierFilter, setSupplierFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState<
    InventorySupplierInvoiceRecord['status'] | 'all'
  >('all');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [itemFilterIds, setItemFilterIds] = useState<string[]>([]);
  const [page, setPage] = useState(1);
  const [pageSize] = useState(20);
  const [isExporting, setIsExporting] = useState(false);

  const [isEditorOpen, setIsEditorOpen] = useState(false);
  const [editingInvoice, setEditingInvoice] =
    useState<InventorySupplierInvoiceRecord | null>(null);
  const [viewingInvoiceId, setViewingInvoiceId] = useState<string | null>(null);
  const [invoiceToDelete, setInvoiceToDelete] =
    useState<InventorySupplierInvoiceRecord | null>(null);
  const [paymentInvoice, setPaymentInvoice] =
    useState<InventorySupplierInvoiceRecord | null>(null);

  const invoiceItemOptions = useInventoryItemFilterOptions({
    companyId,
    status: 'all',
    selectedItemIds: itemFilterIds,
  });
  const invoicesQuery = useInventorySupplierInvoices({
    companyId: companyId ?? 0,
    outletId: effectiveOutletId ?? '',
    supplierId: supplierFilter,
    status: statusFilter === 'all' ? 'all' : statusFilter,
    dateFrom: dateFrom || undefined,
    dateTo: dateTo || undefined,
    inventoryItemIds: itemFilterIds,
    page,
    pageSize,
  });
  const summaryQuery = useInventorySupplierInvoiceSummary(
    companyId,
    effectiveOutletId,
    supplierFilter,
    statusFilter,
    dateFrom,
    dateTo,
    itemFilterIds,
  );
  const suppliersQuery = useInventorySuppliers({
    companyId: companyId ?? 0,
    page: 1,
    pageSize: 500,
    status: 'active',
  });
  const allLocationsQuery = useInventoryStockLocations({
    companyId: companyId ?? 0,
    outletId: effectiveOutletId ?? '',
    page: 1,
    pageSize: 200,
    search: '',
    type: 'all',
    status: 'active',
  });
  const unitsQuery = useInventoryUnits(companyId);
  const viewingDetailQuery = useInventorySupplierInvoiceDetail(
    companyId,
    viewingInvoiceId,
  );

  const paymentMethodsQuery = usePaymentMethods();
  const { mutateAsync: saveInvoiceAsync, isPending: isSaving } =
    useSaveSupplierInvoice();
  const { mutateAsync: postInvoiceAsync, isPending: isPosting } =
    usePostSupplierInvoice();
  const { mutateAsync: deleteInvoiceAsync, isPending: isDeleting } =
    useDeleteSupplierInvoice();
  const { mutateAsync: recordPaymentAsync, isPending: isRecordingPayment } =
    useRecordSupplierPayment();

  const invoices = invoicesQuery.data?.data ?? [];
  const pagination = invoicesQuery.data?.pagination;
  const suppliers = suppliersQuery.data?.data ?? [];
  const allLocations = allLocationsQuery.data?.data ?? [];
  const units = unitsQuery.data ?? [];
  const paymentMethods = (paymentMethodsQuery.data ?? []).filter((pm) =>
    SUPPLIER_PAYMENT_METHOD_TYPES.includes(pm.type),
  );
  const summary: InventorySupplierInvoiceSummaryCounts = summaryQuery.data ?? {
    totalInvoices: 0,
    draftInvoices: 0,
    postedInvoices: 0,
    totalValue: 0,
    totalOutstanding: 0,
  };

  const openCreateModal = () => {
    setEditingInvoice(null);
    setViewingInvoiceId(null);
    setIsEditorOpen(true);
  };

  const openEditModal = (invoice: InventorySupplierInvoiceRecord) => {
    setIsEditorOpen(false);
    setEditingInvoice(invoice);
    setViewingInvoiceId(invoice.id);
    // isEditorOpen is set by the effect below once detail data has loaded
  };

  const openViewModal = (invoice: InventorySupplierInvoiceRecord) => {
    setIsEditorOpen(false);
    setEditingInvoice(invoice);
    setViewingInvoiceId(invoice.id);
  };

  // Open the editor only after detail data is ready, so the form is pre-populated
  useEffect(() => {
    if (editingInvoice && viewingDetailQuery.data && !isEditorOpen) {
      setIsEditorOpen(true);
    }
  }, [editingInvoice, viewingDetailQuery.data, isEditorOpen]);

  const closeEditor = () => {
    if (isSaving || isPosting) return;
    setIsEditorOpen(false);
    setEditingInvoice(null);
    setViewingInvoiceId(null);
  };

  const handleSaveDraft = async (form: SupplierInvoiceFormState) => {
    if (!companyId || !effectiveOutletId) return;
    await saveInvoiceAsync(
      buildSaveSupplierInvoicePayload(
        companyId,
        effectiveOutletId,
        form,
        editingInvoice?.id,
      ),
    );
    closeEditor();
  };

  const handlePost = async (form: SupplierInvoiceFormState) => {
    if (!companyId || !effectiveOutletId) return;

    let targetId = editingInvoice?.id;

    if (!targetId) {
      const saved = await saveInvoiceAsync(
        buildSaveSupplierInvoicePayload(companyId, effectiveOutletId, form),
      );
      if (!saved.success || !saved.data) return;
      targetId = saved.data.id;
    } else if (editingInvoice?.status === 'draft') {
      const saved = await saveInvoiceAsync(
        buildSaveSupplierInvoicePayload(
          companyId,
          effectiveOutletId,
          form,
          targetId,
        ),
      );
      if (!saved.success) return;
    }

    if (!targetId) return;
    await postInvoiceAsync({ companyId, invoiceId: targetId });
    closeEditor();
  };

  const handleDelete = async () => {
    if (!companyId || !invoiceToDelete) return;
    await deleteInvoiceAsync({ companyId, invoiceId: invoiceToDelete.id });
    setInvoiceToDelete(null);
  };

  const handleRecordPayment = async (
    request: Omit<
      import('@/renderer/types/inventory').RecordSupplierPaymentRequest,
      'companyId' | 'outletId'
    >,
  ) => {
    if (!companyId || !effectiveOutletId) return;
    await recordPaymentAsync({
      ...request,
      companyId,
      outletId: effectiveOutletId,
    });
    setPaymentInvoice(null);
  };

  const handleExportCsv = async () => {
    if (!companyId || !effectiveOutletId) {
      apiError('Company or outlet context is missing for CSV export.');
      return;
    }

    setIsExporting(true);

    try {
      const exportRowsResult = await fetchSupplierInvoiceExportRows({
        companyId,
        outletId: effectiveOutletId,
        supplierId: supplierFilter,
        status: statusFilter,
        dateFrom: dateFrom || undefined,
        dateTo: dateTo || undefined,
        inventoryItemIds: itemFilterIds,
      });

      if (!exportRowsResult.success || !exportRowsResult.data) {
        apiError(
          exportRowsResult.error ||
            'Failed to load purchase invoices for CSV export.',
        );
        return;
      }

      if (!exportRowsResult.data.length) {
        apiError(
          'No purchase invoices match the current filters for CSV export.',
        );
        return;
      }

      const response = await exportReportCsv({
        title: 'Purchase invoices',
        fileName: [
          'purchase-invoices',
          sanitizeCsvFileSegment(effectiveOutlet?.name || 'outlet'),
          new Date().toISOString().slice(0, 10),
        ].join('-'),
        columns: SUPPLIER_INVOICE_CSV_COLUMNS,
        rows: buildSupplierInvoiceCsvRows(
          exportRowsResult.data,
          suppliers,
          allLocations,
        ),
      });

      if (!response.success || !response.data) {
        apiError(response.error || 'Failed to export purchase invoices CSV.');
        return;
      }

      apiSuccess('Purchase invoices CSV export completed successfully.');
    } finally {
      setIsExporting(false);
    }
  };

  if (!companyId) {
    return (
      <EmptyState
        title="Purchase invoices unavailable"
        description="We could not find a company context for this workspace."
      />
    );
  }

  if (!effectiveOutletId) {
    return (
      <EmptyState
        title="Choose an outlet first"
        description="Purchase invoices are outlet-specific. Pick an outlet from the header to continue."
      />
    );
  }

  if (invoicesQuery.isLoading) {
    return <LoadingState message="Loading purchase invoices…" />;
  }

  if (invoicesQuery.isError) {
    return (
      <EmptyState
        title="Invoices unavailable"
        description={
          invoicesQuery.error?.message ||
          'Unable to load supplier invoices right now.'
        }
        primaryAction={{
          label: 'Try again',
          onClick: () => invoicesQuery.refetch(),
        }}
      />
    );
  }

  const editorInitial =
    editingInvoice && viewingDetailQuery.data
      ? createSupplierInvoiceFormFromRecord(viewingDetailQuery.data)
      : createEmptySupplierInvoiceForm();

  const isPostedInvoice =
    editingInvoice != null && editingInvoice.status !== 'draft';

  return (
    <div className="space-y-6">
      <SupplierInvoicesToolbar
        supplierFilter={supplierFilter}
        statusFilter={statusFilter}
        dateFrom={dateFrom}
        dateTo={dateTo}
        itemFilterIds={itemFilterIds}
        suppliers={suppliers}
        itemOptions={invoiceItemOptions.options}
        itemOptionsLoading={invoiceItemOptions.isLoading}
        itemOptionsHasMore={invoiceItemOptions.hasMore}
        isExporting={isExporting}
        canExport={invoices.length > 0 || Boolean(pagination?.totalItems)}
        onSupplierChange={(v) => {
          setSupplierFilter(v);
          setPage(1);
        }}
        onStatusChange={(v) => {
          setStatusFilter(v);
          setPage(1);
        }}
        onDateFromChange={(v) => {
          setDateFrom(v);
          setPage(1);
        }}
        onDateToChange={(v) => {
          setDateTo(v);
          setPage(1);
        }}
        onItemFilterChange={(value) => {
          setItemFilterIds(value);
          setPage(1);
        }}
        onItemSearchChange={invoiceItemOptions.setSearchQuery}
        onLoadMoreItems={invoiceItemOptions.loadMore}
        onExportCsv={handleExportCsv}
        onCreate={openCreateModal}
      />

      <SupplierInvoicesSummaryCards summary={summary} />

      {invoices.length ? (
        <>
          <SupplierInvoicesTable
            invoices={invoices}
            suppliers={suppliers}
            locations={allLocations}
            isLoading={invoicesQuery.isFetching && !invoicesQuery.isLoading}
            onView={openViewModal}
            onEdit={openEditModal}
            onDelete={setInvoiceToDelete}
            onRecordPayment={(invoice) => setPaymentInvoice(invoice)}
          />
          {pagination ? (
            <PaginationControls
              pagination={pagination}
              itemLabel="invoices"
              onPageChange={setPage}
            />
          ) : null}
        </>
      ) : (
        <EmptyState
          title="No purchase invoices yet"
          description="Create the first invoice to record a supplier delivery, update stock balances, and track payables."
          primaryAction={{ label: 'Create invoice', onClick: openCreateModal }}
        />
      )}

      {isEditorOpen ? (
        <SupplierInvoiceEditorModal
          companyId={companyId}
          initial={editorInitial}
          isPosted={isPostedInvoice}
          isSaving={isSaving}
          isPosting={isPosting}
          title={
            isPostedInvoice
              ? `Invoice ${editingInvoice?.invoice_number || '(no number)'}`
              : editingInvoice
                ? 'Edit draft invoice'
                : 'Create purchase invoice'
          }
          suppliers={suppliers}
          locations={allLocations}
          units={units}
          onClose={closeEditor}
          onSaveDraft={handleSaveDraft}
          onPost={handlePost}
        />
      ) : null}

      {paymentInvoice ? (
        <SupplierPaymentModal
          invoice={paymentInvoice}
          supplier={suppliers.find((s) => s.id === paymentInvoice.supplier_id)}
          paymentMethods={paymentMethods}
          isSaving={isRecordingPayment}
          onClose={() => setPaymentInvoice(null)}
          onSubmit={handleRecordPayment}
        />
      ) : null}

      <ConfirmDialog
        isOpen={!!invoiceToDelete}
        onClose={() => setInvoiceToDelete(null)}
        onConfirm={handleDelete}
        isLoading={isDeleting}
        title="Delete draft invoice"
        message={`Delete invoice ${invoiceToDelete?.invoice_number || '(no number)'}? This cannot be undone.`}
        confirmText="Delete invoice"
        tone="danger"
      />
    </div>
  );
};

export const SuppliersPage: React.FC = () => {
  const currentUser = useAppSelector(selectCurrentUser);
  const companyId = currentUser?.company?.id ?? null;

  const [searchQuery, setSearchQuery] = useState<string>('');
  const deferredSearchQuery = useDeferredValue(searchQuery);
  const [statusFilter, setStatusFilter] = useState<
    'all' | 'active' | 'inactive'
  >('all');
  const [page, setPage] = useState(1);
  const [pageSize] = useState(20);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingSupplier, setEditingSupplier] =
    useState<InventorySupplierRecord | null>(null);
  const [supplierToDelete, setSupplierToDelete] =
    useState<InventorySupplierRecord | null>(null);

  const suppliersQuery = useInventorySuppliers({
    companyId: companyId ?? 0,
    page,
    pageSize,
    search: deferredSearchQuery,
    status: statusFilter,
  });
  const summaryQuery = useInventorySupplierSummary(
    companyId,
    deferredSearchQuery,
    statusFilter,
  );
  const { mutateAsync: saveSupplierAsync, isPending: isSaving } =
    useSaveInventorySupplier();
  const { mutateAsync: deleteSupplierAsync, isPending: isDeleting } =
    useDeleteInventorySupplier();

  const suppliers = suppliersQuery.data?.data ?? [];
  const pagination = suppliersQuery.data?.pagination;
  const summary: InventorySupplierSummaryCounts = summaryQuery.data ?? {
    totalSuppliers: 0,
    activeSuppliers: 0,
    inactiveSuppliers: 0,
    suppliersWithOpeningBalance: 0,
  };

  const openCreateModal = () => {
    setEditingSupplier(null);
    setIsModalOpen(true);
  };

  const openEditModal = (supplier: InventorySupplierRecord) => {
    setEditingSupplier(supplier);
    setIsModalOpen(true);
  };

  const handleSubmit = async (form: InventorySupplierFormState) => {
    if (!companyId) {
      return;
    }

    await saveSupplierAsync(
      buildSaveInventorySupplierPayload(companyId, form, editingSupplier?.id),
    );
    setIsModalOpen(false);
    setEditingSupplier(null);
  };

  const handleDelete = async () => {
    if (!companyId || !supplierToDelete) {
      return;
    }

    await deleteSupplierAsync({
      companyId,
      supplierId: supplierToDelete.id,
    });
    setSupplierToDelete(null);
  };

  if (!companyId) {
    return (
      <EmptyState
        title="Suppliers unavailable"
        description="We could not find a company context for this inventory workspace."
      />
    );
  }

  if (suppliersQuery.isLoading) {
    return <LoadingState message="Loading suppliers..." />;
  }

  if (suppliersQuery.isError) {
    return (
      <EmptyState
        title="Suppliers unavailable"
        description={
          suppliersQuery.error?.message ||
          'Unable to load inventory suppliers right now.'
        }
        primaryAction={{
          label: 'Try again',
          onClick: () => suppliersQuery.refetch(),
        }}
      />
    );
  }

  return (
    <div className="space-y-6">
      <SuppliersToolbar
        searchQuery={searchQuery}
        statusFilter={statusFilter}
        onSearchChange={(value) => {
          setSearchQuery(value.toString());
          setPage(1);
        }}
        onStatusChange={(value) => {
          setStatusFilter(value);
          setPage(1);
        }}
        onCreate={openCreateModal}
      />

      <SuppliersSummaryCards summary={summary} />

      {suppliers.length ? (
        <>
          <SuppliersTable
            suppliers={suppliers}
            onEdit={openEditModal}
            onDelete={setSupplierToDelete}
          />
          {pagination ? (
            <PaginationControls
              pagination={pagination}
              itemLabel="suppliers"
              onPageChange={setPage}
            />
          ) : null}
        </>
      ) : (
        <EmptyState
          title="No suppliers yet"
          description="Create your first supplier so invoices, payables, and purchasing workflows have a clean master record to use."
          primaryAction={{
            label: 'Create supplier',
            onClick: openCreateModal,
          }}
        />
      )}

      {isModalOpen ? (
        <SupplierEditorModal
          initial={
            editingSupplier
              ? createInventorySupplierFormFromRecord(editingSupplier)
              : createEmptyInventorySupplierForm()
          }
          isSaving={isSaving}
          title={editingSupplier ? 'Edit supplier' : 'Create supplier'}
          onClose={() => {
            if (!isSaving) {
              setIsModalOpen(false);
              setEditingSupplier(null);
            }
          }}
          onSubmit={handleSubmit}
        />
      ) : null}

      <ConfirmDialog
        isOpen={!!supplierToDelete}
        onClose={() => setSupplierToDelete(null)}
        onConfirm={handleDelete}
        isLoading={isDeleting}
        title="Delete supplier"
        message={`Delete ${supplierToDelete?.name || 'this supplier'}? This should only be done if it has not yet been used in purchasing or payable workflows.`}
        confirmText="Delete supplier"
        tone="danger"
      />
    </div>
  );
};

export const SupplierPaymentHistoryPage: React.FC = () => {
  const currentUser = useAppSelector(selectCurrentUser);
  const companyId = currentUser?.company?.id ?? null;
  const effectiveOutletId = useAppSelector(selectEffectiveOutletId);

  const [page, setPage] = useState(1);
  const pageSize = 50;

  const paymentsQuery = useInventorySupplierPayments({
    companyId: companyId ?? 0,
    outletId: effectiveOutletId ?? '',
    page,
    pageSize,
  });
  const suppliersQuery = useInventorySuppliers({
    companyId: companyId ?? 0,
    page: 1,
    pageSize: 500,
    search: '',
    status: 'all',
  });
  const paymentMethodsQuery = usePaymentMethods();

  const payments = paymentsQuery.data?.data ?? [];
  const suppliersPagination = paymentsQuery.data?.pagination;
  const suppliers = suppliersQuery.data?.data ?? [];
  const paymentMethods = paymentMethodsQuery.data ?? [];

  if (!companyId) {
    return (
      <EmptyState
        title="Payment history unavailable"
        description="We could not find a company context for this inventory workspace."
      />
    );
  }

  if (paymentsQuery.isLoading) {
    return <LoadingState message="Loading payment history..." />;
  }

  if (paymentsQuery.isError) {
    return (
      <EmptyState
        title="Payment history unavailable"
        description={
          paymentsQuery.error?.message ||
          'Unable to load supplier payment history right now.'
        }
        primaryAction={{
          label: 'Try again',
          onClick: () => paymentsQuery.refetch(),
        }}
      />
    );
  }

  return (
    <div className="space-y-6">
      {payments.length > 0 ? (
        <>
          <SupplierPaymentsTable
            payments={payments}
            suppliers={suppliers}
            paymentMethods={paymentMethods}
          />
          {suppliersPagination ? (
            <PaginationControls
              pagination={suppliersPagination}
              itemLabel="payments"
              onPageChange={setPage}
            />
          ) : null}
        </>
      ) : (
        <EmptyState
          title="No payment history"
          description="Supplier payments will appear here once invoices have been paid."
        />
      )}
    </div>
  );
};

export const InventoryRecipesPage: React.FC = () => {
  const currentUser = useAppSelector(selectCurrentUser);
  const companyId = currentUser?.company?.id ?? null;
  const effectiveOutletId = useAppSelector(selectEffectiveOutletId);

  const [searchQuery, setSearchQuery] = useState('');
  const deferredSearchQuery = useDeferredValue(searchQuery);
  const [statusFilter, setStatusFilter] = useState<
    'all' | 'active' | 'inactive'
  >('all');
  const [page, setPage] = useState(1);
  const [pageSize] = useState(20);
  const [recipeToDelete, setRecipeToDelete] =
    useState<InventoryRecipeRecord | null>(null);

  const recipesQuery = useInventoryRecipes({
    companyId: companyId ?? 0,
    outletId: effectiveOutletId ?? '',
    page,
    pageSize,
    search: deferredSearchQuery,
    status: statusFilter,
  });
  const summaryQuery = useInventoryRecipeSummary(
    companyId,
    effectiveOutletId ?? '',
    statusFilter,
  );
  const [ingredientCostMap, setIngredientCostMap] = useState<
    Map<string, number>
  >(new Map());

  useEffect(() => {
    if (!companyId) return;
    fetchIngredientCosts(companyId, effectiveOutletId)
      .then(setIngredientCostMap)
      .catch(() => {});
  }, [companyId, effectiveOutletId]);

  const { mutateAsync: deleteRecipeAsync, isPending: isDeleting } =
    useDeleteRecipe();

  const recipes = recipesQuery.data?.data ?? [];
  const pagination = recipesQuery.data?.pagination;
  const summary: InventoryRecipeSummaryCounts = summaryQuery.data ?? {
    totalRecipes: 0,
    activeRecipes: 0,
    inactiveRecipes: 0,
    linkedMenuItems: 0,
  };

  useEffect(() => {
    setPage(1);
  }, [deferredSearchQuery, statusFilter, effectiveOutletId]);

  const { isModalOpen, openCreateModal, openEditModal, modalProps } =
    useRecipeEditorController(companyId, effectiveOutletId);
  const menuItems = modalProps?.menuItems ?? [];

  const handleDelete = async () => {
    if (!companyId || !recipeToDelete) return;
    await deleteRecipeAsync({ companyId, recipeId: recipeToDelete.id });
    setRecipeToDelete(null);
  };

  if (!companyId) {
    return (
      <EmptyState
        title="Recipes unavailable"
        description="We could not find a company context for this inventory workspace."
      />
    );
  }

  if (!effectiveOutletId) {
    return (
      <EmptyState
        title="Select an outlet"
        description="Choose an outlet before creating or reviewing recipes."
      />
    );
  }

  if (recipesQuery.isLoading) {
    return <LoadingState message="Loading recipes…" />;
  }

  if (recipesQuery.isError) {
    return (
      <EmptyState
        title="Recipes unavailable"
        description={
          recipesQuery.error?.message || 'Unable to load recipes right now.'
        }
        primaryAction={{
          label: 'Try again',
          onClick: () => recipesQuery.refetch(),
        }}
      />
    );
  }

  return (
    <div className="space-y-6">
      <RecipesToolbar
        searchQuery={searchQuery}
        statusFilter={statusFilter}
        onSearchChange={(value) => {
          setSearchQuery(value.toString());
          setPage(1);
        }}
        onStatusChange={(value) => {
          setStatusFilter(value);
          setPage(1);
        }}
        onCreate={openCreateModal}
      />

      <RecipesSummaryCards summary={summary} />

      {recipes.length ? (
        <>
          <RecipesTable
            recipes={recipes}
            menuItems={menuItems}
            isLoading={recipesQuery.isFetching && !recipesQuery.isLoading}
            costMap={ingredientCostMap}
            onEdit={openEditModal}
            onDelete={setRecipeToDelete}
          />
          {pagination ? (
            <PaginationControls
              pagination={pagination}
              itemLabel="recipes"
              onPageChange={setPage}
            />
          ) : null}
        </>
      ) : (
        <EmptyState
          title="No recipes yet"
          description="Create your first recipe to define which inventory ingredients are consumed when a menu item is sold."
          primaryAction={{
            label: 'Create recipe',
            onClick: openCreateModal,
          }}
        />
      )}

      {isModalOpen && modalProps ? <RecipeEditorModal {...modalProps} /> : null}

      <ConfirmDialog
        isOpen={!!recipeToDelete}
        onClose={() => setRecipeToDelete(null)}
        onConfirm={handleDelete}
        isLoading={isDeleting}
        title="Delete recipe"
        message={`Delete recipe "${recipeToDelete?.name || 'this recipe'}"? This cannot be undone.`}
        confirmText="Delete recipe"
        tone="danger"
      />
    </div>
  );
};

// ─── Stock Transfers ─────────────────────────────────────────────────────────

const TRANSFER_STATUS_LABELS: Record<StockTransferRecord['status'], string> = {
  draft: 'Draft',
  posted: 'Posted',
  cancelled: 'Cancelled',
};

const TRANSFER_STATUS_COLORS: Record<StockTransferRecord['status'], string> = {
  draft: 'bg-yellow-100 text-yellow-700',
  posted: 'bg-green-100 text-green-700',
  cancelled: 'bg-gray-100 text-gray-500',
};

export const StockTransfersPage: React.FC = () => {
  const currentUser = useAppSelector(selectCurrentUser);
  const effectiveOutletId = useAppSelector(selectEffectiveOutletId);
  const companyId = currentUser?.company?.id ?? null;

  const [isEditorOpen, setIsEditorOpen] = useState(false);
  const [editingTransfer, setEditingTransfer] =
    useState<StockTransferRecord | null>(null);
  const [postingId, setPostingId] = useState<string | null>(null);
  const [transferToPost, setTransferToPost] =
    useState<StockTransferRecord | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const deferredSearch = useDeferredValue(searchQuery);
  const [statusFilter, setStatusFilter] = useState<
    StockTransferRecord['status'] | 'all'
  >('all');
  const [page, setPage] = useState(1);
  const pageSize = 20;

  const transfersQuery = useStockTransfers({
    companyId: companyId ?? 0,
    outletId: effectiveOutletId ?? '',
    page,
    pageSize,
    search: deferredSearch,
    status: statusFilter,
  });
  const summaryQuery = useStockTransferSummary(companyId, effectiveOutletId);
  const locationsQuery = useInventoryStockLocations({
    companyId: companyId ?? 0,
    outletId: effectiveOutletId ?? '',
    page: 1,
    pageSize: 200,
    search: '',
    type: 'all',
    status: 'active',
  });
  const unitsQuery = useInventoryUnits(companyId);
  const transferDetailQuery = useStockTransferDetail(
    companyId,
    isEditorOpen ? (editingTransfer?.id ?? null) : null,
  );

  const saveMutation = useSaveStockTransfer();
  const postMutation = usePostStockTransfer();

  const transfers = transfersQuery.data?.data ?? [];
  const pagination = transfersQuery.data?.pagination;
  const summary = summaryQuery.data;
  const modalTransfer = editingTransfer
    ? (transferDetailQuery.data ?? editingTransfer)
    : null;
  const locations = locationsQuery.data?.data ?? [];
  const units = unitsQuery.data ?? [];

  if (!companyId || !effectiveOutletId) {
    return (
      <EmptyState
        title="Choose an outlet"
        description="Stock transfers are outlet-specific. Pick an outlet from the header."
      />
    );
  }

  const openCreate = () => {
    setEditingTransfer(null);
    setIsEditorOpen(true);
  };

  const openEdit = (t: StockTransferRecord) => {
    setEditingTransfer(t);
    setIsEditorOpen(true);
  };

  const handlePost = async (transferId: string) => {
    setPostingId(transferId);
    await postMutation.mutateAsync(transferId);
    setPostingId(null);
  };

  return (
    <div className="space-y-6">
      {/* Summary cards */}
      <div className="grid grid-cols-3 gap-4">
        {[
          { label: 'Total', value: summary?.totalTransfers ?? 0 },
          { label: 'Draft', value: summary?.draftTransfers ?? 0 },
          { label: 'Posted', value: summary?.postedTransfers ?? 0 },
        ].map((card) => (
          <div
            key={card.label}
            className="rounded-2xl border border-gray-200 bg-white p-4"
          >
            <p className="text-xs font-medium uppercase tracking-wide text-gray-500">
              {card.label}
            </p>
            <p className="mt-1 text-2xl font-bold text-black">{card.value}</p>
          </div>
        ))}
      </div>

      {/* Toolbar */}
      <div className="flex items-center gap-3">
        <input
          type="text"
          value={searchQuery}
          onChange={(e) => {
            setSearchQuery(e.target.value);
            setPage(1);
          }}
          placeholder="Search by reference or notes..."
          className="flex-1 rounded-xl border border-gray-200 bg-white px-4 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-pos-primary/30"
        />
        <DropdownSelect
          value={statusFilter}
          onChange={(v) => {
            setStatusFilter(v as StockTransferRecord['status'] | 'all');
            setPage(1);
          }}
          options={[
            { value: 'all', label: 'All statuses' },
            { value: 'draft', label: 'Draft' },
            { value: 'posted', label: 'Posted' },
            { value: 'cancelled', label: 'Cancelled' },
          ]}
        />
        <button
          type="button"
          onClick={openCreate}
          className="rounded-xl bg-pos-primary px-4 py-2 text-sm font-semibold text-white hover:bg-pos-primary/90"
        >
          + New Transfer
        </button>
      </div>

      {/* Table */}
      {transfersQuery.isLoading ? (
        <LoadingState message="Loading transfers..." />
      ) : transfers.length === 0 ? (
        <EmptyState
          title="No transfers found"
          description="Create a stock transfer to move items between locations."
        />
      ) : (
        <div className="overflow-hidden rounded-2xl border border-gray-200 bg-white">
          <div className="max-h-[34rem] overflow-auto">
            <table className="w-full text-sm">
              <thead className="sticky top-0 border-b border-gray-100 bg-gray-50">
                <tr className="text-left">
                  <th className="px-5 py-3 text-xs font-semibold uppercase tracking-wide text-gray-500">
                    Date
                  </th>
                  <th className="px-5 py-3 text-xs font-semibold uppercase tracking-wide text-gray-500">
                    Reference
                  </th>
                  <th className="px-5 py-3 text-xs font-semibold uppercase tracking-wide text-gray-500">
                    From → To
                  </th>
                  <th className="px-5 py-3 text-xs font-semibold uppercase tracking-wide text-gray-500">
                    Status
                  </th>
                  <th className="px-5 py-3 text-xs font-semibold uppercase tracking-wide text-gray-500">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody>
                {transfers.map((t) => {
                  const fromLoc = locations.find(
                    (l) => l.id === t.from_stock_location_id,
                  );
                  const toLoc = locations.find(
                    (l) => l.id === t.to_stock_location_id,
                  );
                  return (
                    <tr key={t.id} className="border-t border-gray-100">
                      <td className="px-5 py-4 text-gray-600">
                        {t.transfer_date}
                      </td>
                      <td className="px-5 py-4 text-gray-900">
                        {t.reference ?? '—'}
                      </td>
                      <td className="px-5 py-4 text-gray-600">
                        {fromLoc?.name ?? '…'} → {toLoc?.name ?? '…'}
                      </td>
                      <td className="px-5 py-4">
                        <span
                          className={`rounded-full px-2 py-0.5 text-xs font-semibold ${TRANSFER_STATUS_COLORS[t.status]}`}
                        >
                          {TRANSFER_STATUS_LABELS[t.status]}
                        </span>
                      </td>
                      <td className="px-5 py-4">
                        <div className="flex gap-2">
                          {t.status === 'draft' ? (
                            <>
                              <button
                                type="button"
                                onClick={() => openEdit(t)}
                                className="text-xs font-medium text-pos-primary hover:underline"
                              >
                                Edit
                              </button>
                              <button
                                type="button"
                                disabled={postingId === t.id}
                                onClick={() => setTransferToPost(t)}
                                className="text-xs font-medium text-green-600 hover:underline disabled:opacity-50"
                              >
                                {postingId === t.id ? 'Posting…' : 'Post'}
                              </button>
                            </>
                          ) : (
                            <button
                              type="button"
                              onClick={() => openEdit(t)}
                              className="text-xs font-medium text-gray-600 hover:underline"
                            >
                              View
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          {pagination && pagination.totalPages > 1 && (
            <div className="border-t border-gray-100 px-5 py-3">
              <PaginationControls
                pagination={pagination}
                itemLabel="transfers"
                onPageChange={setPage}
              />
            </div>
          )}
        </div>
      )}

      <ConfirmDialog
        isOpen={!!transferToPost}
        onClose={() => setTransferToPost(null)}
        onConfirm={async () => {
          if (!transferToPost) return;
          await handlePost(transferToPost.id);
          setTransferToPost(null);
        }}
        isLoading={postingId === transferToPost?.id}
        title="Post stock transfer"
        message={`Post this transfer${transferToPost?.reference ? ` (${transferToPost.reference})` : ''}? Stock balances will update immediately and this cannot be undone.`}
        confirmText="Post transfer"
        tone="default"
      />

      {isEditorOpen && (
        <StockTransferEditorModal
          transfer={modalTransfer}
          isLoadingDetail={
            transferDetailQuery.isFetching && !transferDetailQuery.data
          }
          locations={locations}
          units={units}
          companyId={companyId}
          outletId={effectiveOutletId}
          isSaving={saveMutation.isPending}
          onSave={async (req) => {
            await saveMutation.mutateAsync(req);
            setIsEditorOpen(false);
          }}
          onClose={() => setIsEditorOpen(false)}
        />
      )}
    </div>
  );
};

// ─── Stock Adjustments ───────────────────────────────────────────────────────

const ADJUSTMENT_TYPE_LABELS: Record<
  StockAdjustmentRecord['adjustment_type'],
  string
> = {
  variance: 'Variance',
  wastage: 'Wastage',
  correction: 'Correction',
  stock_count: 'Stock Count',
  internal_use: 'Internal Use / Consumption',
};

const ADJUSTMENT_STATUS_COLORS: Record<
  StockAdjustmentRecord['status'],
  string
> = {
  draft: 'bg-yellow-100 text-yellow-700',
  posted: 'bg-green-100 text-green-700',
  cancelled: 'bg-gray-100 text-gray-500',
};

export const StockAdjustmentsPage: React.FC = () => {
  const currentUser = useAppSelector(selectCurrentUser);
  const effectiveOutletId = useAppSelector(selectEffectiveOutletId);
  const companyId = currentUser?.company?.id ?? null;

  const [isEditorOpen, setIsEditorOpen] = useState(false);
  const [editingAdj, setEditingAdj] = useState<StockAdjustmentRecord | null>(
    null,
  );
  const [postingId, setPostingId] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const deferredSearch = useDeferredValue(searchQuery);
  const [statusFilter, setStatusFilter] = useState<
    StockAdjustmentRecord['status'] | 'all'
  >('all');
  const [typeFilter, setTypeFilter] = useState<
    StockAdjustmentRecord['adjustment_type'] | 'all'
  >('all');
  const [page, setPage] = useState(1);
  const pageSize = 20;

  const adjustmentsQuery = useStockAdjustments({
    companyId: companyId ?? 0,
    outletId: effectiveOutletId ?? '',
    page,
    pageSize,
    search: deferredSearch,
    status: statusFilter,
    adjustmentType: typeFilter,
  });
  const summaryQuery = useStockAdjustmentSummary(companyId, effectiveOutletId);
  const locationsQuery = useInventoryStockLocations({
    companyId: companyId ?? 0,
    outletId: effectiveOutletId ?? '',
    page: 1,
    pageSize: 200,
    search: '',
    type: 'all',
    status: 'active',
  });
  const saveMutation = useSaveStockAdjustment();
  const postMutation = usePostStockAdjustment();

  const adjustments = adjustmentsQuery.data?.data ?? [];
  const pagination = adjustmentsQuery.data?.pagination;
  const summary = summaryQuery.data;
  const locations = locationsQuery.data?.data ?? [];

  if (!companyId || !effectiveOutletId) {
    return (
      <EmptyState
        title="Choose an outlet"
        description="Stock adjustments are outlet-specific. Pick an outlet from the header."
      />
    );
  }

  const openCreate = () => {
    setEditingAdj(null);
    setIsEditorOpen(true);
  };

  const openEdit = (adj: StockAdjustmentRecord) => {
    setEditingAdj(adj);
    setIsEditorOpen(true);
  };

  const handlePost = async (adjustmentId: string) => {
    setPostingId(adjustmentId);
    await postMutation.mutateAsync(adjustmentId);
    setPostingId(null);
  };

  return (
    <div className="space-y-6">
      {/* Summary cards */}
      <div className="grid grid-cols-4 gap-4">
        {[
          { label: 'Total', value: summary?.totalAdjustments ?? 0 },
          { label: 'Draft', value: summary?.draftAdjustments ?? 0 },
          { label: 'Posted', value: summary?.postedAdjustments ?? 0 },
          { label: 'Wastage', value: summary?.wastageAdjustments ?? 0 },
        ].map((card) => (
          <div
            key={card.label}
            className="rounded-2xl border border-gray-200 bg-white p-4"
          >
            <p className="text-xs font-medium uppercase tracking-wide text-gray-500">
              {card.label}
            </p>
            <p className="mt-1 text-2xl font-bold text-black">{card.value}</p>
          </div>
        ))}
      </div>

      {/* Toolbar */}
      <div className="flex items-center gap-3">
        <input
          type="text"
          value={searchQuery}
          onChange={(e) => {
            setSearchQuery(e.target.value);
            setPage(1);
          }}
          placeholder="Search by reference or notes..."
          className="flex-1 rounded-xl border border-gray-200 bg-white px-4 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-pos-primary/30"
        />
        <DropdownSelect
          value={typeFilter}
          onChange={(v) => {
            setTypeFilter(
              v as StockAdjustmentRecord['adjustment_type'] | 'all',
            );
            setPage(1);
          }}
          options={[
            { value: 'all', label: 'All types' },
            { value: 'correction', label: 'Correction' },
            { value: 'wastage', label: 'Wastage' },
            { value: 'variance', label: 'Variance' },
            { value: 'stock_count', label: 'Stock Count' },
            { value: 'internal_use', label: 'Internal Use / Consumption' },
          ]}
        />
        <DropdownSelect
          value={statusFilter}
          onChange={(v) => {
            setStatusFilter(v as StockAdjustmentRecord['status'] | 'all');
            setPage(1);
          }}
          options={[
            { value: 'all', label: 'All statuses' },
            { value: 'draft', label: 'Draft' },
            { value: 'posted', label: 'Posted' },
          ]}
        />
        <button
          type="button"
          onClick={openCreate}
          className="rounded-xl bg-pos-primary px-4 py-2 text-sm font-semibold text-white hover:bg-pos-primary/90"
        >
          + New Adjustment
        </button>
      </div>

      {/* Table */}
      {adjustmentsQuery.isLoading ? (
        <LoadingState message="Loading adjustments..." />
      ) : adjustments.length === 0 ? (
        <EmptyState
          title="No adjustments found"
          description="Record stock adjustments, wastage, or count corrections here."
        />
      ) : (
        <div className="overflow-hidden rounded-2xl border border-gray-200 bg-white">
          <div className="max-h-[34rem] overflow-auto">
            <table className="w-full text-sm">
              <thead className="sticky top-0 border-b border-gray-100 bg-gray-50">
                <tr className="text-left">
                  <th className="px-5 py-3 text-xs font-semibold uppercase tracking-wide text-gray-500">
                    Date
                  </th>
                  <th className="px-5 py-3 text-xs font-semibold uppercase tracking-wide text-gray-500">
                    Type
                  </th>
                  <th className="px-5 py-3 text-xs font-semibold uppercase tracking-wide text-gray-500">
                    Reference
                  </th>
                  <th className="px-5 py-3 text-xs font-semibold uppercase tracking-wide text-gray-500">
                    Location
                  </th>
                  <th className="px-5 py-3 text-xs font-semibold uppercase tracking-wide text-gray-500">
                    Status
                  </th>
                  <th className="px-5 py-3 text-xs font-semibold uppercase tracking-wide text-gray-500">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody>
                {adjustments.map((adj) => {
                  const loc = locations.find(
                    (l) => l.id === adj.stock_location_id,
                  );
                  return (
                    <tr key={adj.id} className="border-t border-gray-100">
                      <td className="px-5 py-4 text-gray-600">
                        {adj.adjustment_date}
                      </td>
                      <td className="px-5 py-4 text-gray-900">
                        {ADJUSTMENT_TYPE_LABELS[adj.adjustment_type]}
                      </td>
                      <td className="px-5 py-4 text-gray-600">
                        {adj.reference ?? '—'}
                      </td>
                      <td className="px-5 py-4 text-gray-600">
                        {loc?.name ?? '—'}
                      </td>
                      <td className="px-5 py-4">
                        <span
                          className={`rounded-full px-2 py-0.5 text-xs font-semibold ${ADJUSTMENT_STATUS_COLORS[adj.status]}`}
                        >
                          {adj.status.charAt(0).toUpperCase() +
                            adj.status.slice(1)}
                        </span>
                      </td>
                      <td className="px-5 py-4">
                        <div className="flex gap-2">
                          {adj.status === 'draft' && (
                            <>
                              <button
                                type="button"
                                onClick={() => openEdit(adj)}
                                className="text-xs font-medium text-pos-primary hover:underline"
                              >
                                Edit
                              </button>
                              <button
                                type="button"
                                disabled={postingId === adj.id}
                                onClick={() => handlePost(adj.id)}
                                className="text-xs font-medium text-green-600 hover:underline disabled:opacity-50"
                              >
                                {postingId === adj.id ? 'Posting…' : 'Post'}
                              </button>
                            </>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          {pagination && pagination.totalPages > 1 && (
            <div className="border-t border-gray-100 px-5 py-3">
              <PaginationControls
                pagination={pagination}
                itemLabel="adjustments"
                onPageChange={setPage}
              />
            </div>
          )}
        </div>
      )}

      {isEditorOpen && (
        <StockAdjustmentEditorModal
          adjustment={editingAdj}
          locations={locations}
          companyId={companyId}
          outletId={effectiveOutletId}
          isSaving={saveMutation.isPending}
          onSave={async (req) => {
            await saveMutation.mutateAsync(req);
            setIsEditorOpen(false);
          }}
          onClose={() => setIsEditorOpen(false)}
        />
      )}
    </div>
  );
};

export const InventoryMovementHistoryReportPage: React.FC = () => (
  <StockMovementsPage />
);

export const InventoryMovementSummaryReportPage: React.FC = () => (
  <StockMovementSummaryPage />
);

export const InventoryCategoriesPage: React.FC = () => (
  <InventoryCategoriesPageContent />
);

export const InventoryUnitsPage: React.FC = () => <InventoryUnitsPageContent />;

export const LowStockItemsPage: React.FC = () => {
  const currentUser = useAppSelector(selectCurrentUser);
  const effectiveOutletId = useAppSelector(selectEffectiveOutletId);
  const effectiveOutlet = useAppSelector(selectEffectiveOutlet);
  const companyId = currentUser?.company?.id ?? null;

  const [itemFilterIds, setItemFilterIds] = useState<string[]>([]);
  const [locationFilter, setLocationFilter] = useState('all');
  const [page, setPage] = useState(1);
  const [isExporting, setIsExporting] = useState(false);

  const lowStockItemOptions = useInventoryItemFilterOptions({
    companyId,
    status: 'active',
    selectedItemIds: itemFilterIds,
  });

  const locationsQuery = useInventoryStockLocations({
    companyId: companyId ?? 0,
    outletId: effectiveOutletId ?? '',
    page: 1,
    pageSize: 200,
    search: '',
    type: 'all',
    status: 'active',
  });
  const locations = locationsQuery.data?.data ?? [];

  const query = useInventoryLowStockItems({
    companyId: companyId ?? 0,
    outletId: effectiveOutletId,
    stockLocationId: locationFilter,
    inventoryItemIds: itemFilterIds,
    page,
    pageSize: 50,
  });

  const rows = query.data?.data ?? [];
  const pagination = query.data?.pagination;

  if (!companyId) {
    return (
      <EmptyState
        title="Low-stock items unavailable"
        description="We could not find a company context for this inventory workspace."
      />
    );
  }

  const handleExportCsv = async () => {
    if (!companyId) {
      apiError('Company context is missing for CSV export.');
      return;
    }

    setIsExporting(true);

    try {
      const exportRowsResult = await fetchLowStockItemExportRows({
        companyId,
        outletId: effectiveOutletId,
        stockLocationId: locationFilter,
        inventoryItemIds: itemFilterIds,
      });

      if (!exportRowsResult.success || !exportRowsResult.data) {
        apiError(
          exportRowsResult.error ||
            'Failed to load low-stock items for CSV export.',
        );
        return;
      }

      if (!exportRowsResult.data.length) {
        apiError(
          'No low-stock items match the current filters for CSV export.',
        );
        return;
      }

      const response = await exportReportCsv({
        title: 'Low-stock items',
        fileName: [
          'low-stock-items',
          sanitizeCsvFileSegment(effectiveOutlet?.name || 'outlet'),
          new Date().toISOString().slice(0, 10),
        ].join('-'),
        columns: LOW_STOCK_CSV_COLUMNS,
        rows: buildLowStockCsvRows(exportRowsResult.data),
      });

      if (!response.success || !response.data) {
        apiError(response.error || 'Failed to export low-stock items CSV.');
        return;
      }

      apiSuccess('Low-stock items CSV export completed successfully.');
    } finally {
      setIsExporting(false);
    }
  };

  return (
    <div className="space-y-6">
      {/* Toolbar */}
      <div className="rounded-2xl border border-gray-200 bg-white p-4 shadow-sm">
        <div className="flex flex-col gap-4 md:flex-row md:items-end md:justify-between">
          <div>
            <h3 className="text-lg font-semibold text-gray-900">
              Low-Stock Items
            </h3>
            <p className="mt-1 text-sm text-gray-500">
              Items currently below their reorder level. Replenish stock to
              avoid fulfilment gaps.
            </p>
          </div>
          <div className="flex w-full flex-col gap-2 md:w-auto md:flex-row">
            <Button
              variant="outline"
              onClick={handleExportCsv}
              isLoading={isExporting}
              disabled={!rows.length && !pagination?.totalItems}
              className="min-w-[140px] h-fit"
            >
              Export CSV
            </Button>
            <div className="w-full md:min-w-[320px]">
              <DropdownSelect
                label=""
                values={itemFilterIds}
                options={lowStockItemOptions.options}
                placeholder="All inventory items"
                customDisplayLabel={
                  itemFilterIds.length
                    ? `${itemFilterIds.length} item${itemFilterIds.length === 1 ? '' : 's'} selected`
                    : undefined
                }
                multiple
                searchable
                searchPlaceholder="Search items"
                onSearchChange={lowStockItemOptions.setSearchQuery}
                hasMoreOptions={lowStockItemOptions.hasMore}
                isLoadingOptions={lowStockItemOptions.isLoading}
                onLoadMoreOptions={lowStockItemOptions.loadMore}
                emptyStateLabel="No items found."
                onMultiChange={(value) => {
                  setItemFilterIds(value);
                  setPage(1);
                }}
              />
            </div>
            <div className="w-full md:min-w-[200px]">
              <DropdownSelect
                label=""
                value={locationFilter}
                options={[
                  { value: 'all', label: 'All locations' },
                  ...locations.map((location) => ({
                    value: location.id,
                    label: location.name,
                  })),
                ]}
                onChange={(value) => {
                  setLocationFilter(value);
                  setPage(1);
                }}
              />
            </div>
          </div>
        </div>
      </div>

      {query.isLoading && rows.length === 0 ? (
        <LoadingState message="Loading low-stock items…" />
      ) : rows.length === 0 ? (
        <EmptyState
          title="No low-stock items"
          description={
            itemFilterIds.length
              ? 'None of the selected items are currently below their reorder level.'
              : 'All tracked items are currently above their reorder levels.'
          }
          icon="package"
        />
      ) : (
        <>
          <div className="overflow-hidden rounded-2xl border border-gray-200 bg-white">
            <div className="relative h-[34rem] overflow-auto">
              {query.isFetching && !query.isLoading ? (
                <div className="pointer-events-none absolute inset-0 z-20 flex items-start justify-center bg-white/65 pt-5">
                  <span className="inline-flex items-center gap-2 rounded-full border border-emerald-100 bg-white px-3 py-1.5 text-xs font-semibold text-emerald-700 shadow-sm">
                    Updating low-stock items
                  </span>
                </div>
              ) : null}
              <table
                className={`w-full text-sm transition-opacity ${query.isFetching && !query.isLoading ? 'opacity-60' : ''}`}
                aria-busy={query.isFetching && !query.isLoading}
              >
                <thead className="sticky top-0 border-b border-gray-100 bg-gray-50">
                  <tr className="text-left">
                    <th className="px-5 py-3 text-xs font-semibold uppercase tracking-wide text-gray-500">
                      Item
                    </th>
                    <th className="px-5 py-3 text-xs font-semibold uppercase tracking-wide text-gray-500">
                      Location
                    </th>
                    <th className="px-5 py-3 text-right text-xs font-semibold uppercase tracking-wide text-gray-500">
                      On hand
                    </th>
                    <th className="px-5 py-3 text-right text-xs font-semibold uppercase tracking-wide text-gray-500">
                      Reorder level
                    </th>
                    <th className="px-5 py-3 text-right text-xs font-semibold uppercase tracking-wide text-gray-500">
                      Deficit
                    </th>
                    <th className="px-5 py-3 text-right text-xs font-semibold uppercase tracking-wide text-gray-500">
                      Stock value
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((row) => (
                    <tr
                      key={`${row.stock_location_id}-${row.inventory_item_id}`}
                      className="border-t border-gray-100"
                    >
                      <td className="px-5 py-4">
                        <div className="font-medium text-gray-900">
                          {row.item_name}
                        </div>
                        {row.sku && (
                          <div className="text-xs text-gray-500">{row.sku}</div>
                        )}
                      </td>
                      <td className="px-5 py-4 text-gray-600">
                        {row.stock_location_name}
                      </td>
                      <td className="px-5 py-4 text-right font-medium text-amber-700">
                        {row.quantity_on_hand.toLocaleString(undefined, {
                          maximumFractionDigits: 4,
                        })}
                      </td>
                      <td className="px-5 py-4 text-right text-gray-600">
                        {row.reorder_level.toLocaleString(undefined, {
                          maximumFractionDigits: 4,
                        })}
                      </td>
                      <td className="px-5 py-4 text-right font-semibold text-red-600">
                        {row.deficit_quantity.toLocaleString(undefined, {
                          maximumFractionDigits: 4,
                        })}
                      </td>
                      <td className="px-5 py-4 text-right text-gray-600">
                        {formatReportMoney(row.total_value)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
          {pagination && (
            <PaginationControls
              pagination={pagination}
              itemLabel="items"
              onPageChange={setPage}
            />
          )}
        </>
      )}
    </div>
  );
};
